import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Layout } from '@/components/layout';
import { fmtDate } from '@/lib/dateFormat';
import { ShopifyTargetStoreSelector } from '@/components/shopify-target-store';
import { Button } from '@/components/ui-elements';
import { useAuthStore, useAuthHeaders } from '@/lib/auth-store';
import { useToast } from '@/hooks/use-toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import {
  Search,
  Package,
  CheckCircle,
  AlertCircle,
  Loader2,
  Car,
  ChevronDown,
  ChevronUp,
  ShoppingBag,
  Check,
  Info,
  Warehouse,
  RefreshCw,
  Eye,
  History,
  ChevronRight,
} from 'lucide-react';

const BASE = import.meta.env.BASE_URL.replace(/\/$/, '');

interface WPProduct {
  sku: string;
  partNumber?: string;
  brand?: string;
  productLine?: string;
  category?: string;
  productCategory?: string;
  type?: string;
  productType?: string;
  description?: string;
  finish?: string;
  abbreviatedFinishDesc?: string;
  finishWarranty?: string;
  structuralWarranty?: string;
  diameter?: number | string;
  width?: number | string;
  boltPattern?: string;
  boltPatternStandard?: string;
  lugCount?: number;
  offset?: number | string;
  centerbore?: number | string;
  backspacing?: number | string;
  lipDepth?: number;
  sizeDesc?: string;
  loadRating?: number | string;
  loadRatingMetric?: number;
  loadRatingStandard?: number;
  weight?: number | string;
  constructionType?: string;
  material?: string;
  tpmsCompatible?: boolean;
  certification?: string;
  capPartNo?: string;
  rivetPartNo?: string;
  rivetQuantity?: number;
  upc?: string;
  mapPrice?: number | string;
  msrp?: number | string;
  nip?: number | string;
  images?: string[];
  /** Inline stock fields returned by WP search (no Shopify required) */
  localStock?: number;
  globalStock?: number;
  /** CSV-enriched fields — style name, brand, and finish from the catalog */
  csvStyle?: string;
  csvBrand?: string;
  csvFinish?: string;
}

interface WPPricing {
  sku: string;
  price?: number | string;
  mapPrice?: number | string;
  msrp?: number | string;
  /** NIP = Net Invoice Price / dealer cost */
  nip?: number | string;
  cost?: number | string;
}

interface ShopifyStore {
  id: string;
  nickname: string;
}

/** A single warehouse inventory row returned by the API. */
interface WPInventoryRow {
  sku: string;
  warehouseCode?: string;
  warehouseName?: string;
  quantityOnHand?: number;
  quantityAvailable?: number;
}

interface SearchParams {
  brand: string;
  sku: string;
  partNumber: string;
  finish: string;
  diameter: string;
  width: string;
  boltPattern: string;
  minOffset: string;
  maxOffset: string;
  availabilityType: 'ALL' | 'AVAILABLE' | 'LOCAL';
  year: string;
  make: string;
  model: string;
  trim: string;
}

// ── Grouped product helpers ────────────────────────────────────────────────

interface WPProductGroup {
  groupKey: string;
  partNumber?: string;
  brand?: string;
  genericTitle: string;
  finishes: string[];
  image?: string;
  skus: WPProduct[];
}

/** Build a clean "<brand> <style>" title — prefers CSV style name when available */
function deriveGenericTitle(p: WPProduct): string {
  // CSV style is the most accurate: "KM775 ROCKSTAR CAR", "D255 FULL BLOWN", etc.
  if (p.csvStyle) {
    const brand = (p.csvBrand || p.brand || '').trim();
    return brand ? `${brand} ${p.csvStyle}` : p.csvStyle;
  }
  // Fallback: use productLine as-is — it usually contains model number + style name,
  // e.g. "KM675 SPLICE", "D255 FULL BLOWN", "AB039 MOGUL 5".
  // However some WP API responses return only the style name (e.g. "SPLICE") without
  // the model code prefix. Detect this and prepend the model code from partNumber/sku.
  const pl = (p.productLine ?? '').trim();
  const brand = (p.brand ?? '').trim();

  if (pl) {
    const startsWithModelCode = /^[A-Za-z]+\d{2,4}/.test(pl);
    if (!startsWithModelCode) {
      const partN = (p.partNumber ?? p.sku ?? '').trim();
      const rawFirst = partN.split(/\s+/)[0] ?? '';
      const modelCode = partN.includes(' ')
        ? rawFirst
        : (rawFirst.match(/^([A-Za-z]+\d{2,4})/)?.[1] ?? '');
      if (modelCode) {
        const full = `${modelCode} ${pl}`;
        return brand ? `${brand} ${full}` : full;
      }
    }
    return brand ? `${brand} ${pl}` : pl;
  }
  return brand || p.sku;
}

/** Convert imperial bolt-pattern inch values to metric and normalise separator to lowercase "x" */
const BP_INCH_TO_MM: Record<string, string> = {
  "3.93": "100",
  "4.0": "101.6",   "4.00": "101.6",
  "4.25": "108",    "4.33": "110",    "4.41": "112",
  "4.5": "114.3",   "4.50": "114.3",  "4.52": "115",    "4.53": "156",
  "4.72": "120",    "4.75": "120.65",
  "5.0": "127",     "5.00": "127",    "5.12": "130",    "5.31": "135",
  "5.35": "136",    "5.5": "139.7",   "5.50": "139.7",  "5.67": "144",
  "5.91": "150",    "6.5": "165.1",   "6.50": "165.1",  "6.69": "170",
  "7.09": "180",    "7.87": "200",    "8.07": "205",    "8.26": "210",
};
function normalizeBp(v: string | undefined | null): string {
  const raw = (v ?? "").trim();
  if (!raw || raw.toUpperCase() === "BLANK") return "";
  // Normalise all separators to lowercase "x" first
  const norm = raw.replace(/[Xx]/g, "x");
  return norm.replace(/^(\d+)x(\d+\.\d+)$/, (_f, lugs: string, inches: string) => {
    const mm = BP_INCH_TO_MM[inches];
    return mm ? `${lugs}x${mm}` : `${lugs}x${inches}`;
  });
}

/** Format a number removing trailing ".0" or ".00" decimals */
function fmtNum(v: string | number | undefined): string {
  if (v == null || v === '') return '';
  const n = parseFloat(String(v));
  return isNaN(n) ? String(v) : String(n);
}


/** Group flat product list by partNumber (or individual SKU as fallback) */
function groupProducts(products: WPProduct[]): WPProductGroup[] {
  const map = new Map<string, WPProduct[]>();
  for (const p of products) {
    const key = p.partNumber || p.sku;
    const arr = map.get(key) ?? [];
    arr.push(p);
    map.set(key, arr);
  }
  return Array.from(map.entries()).map(([key, skus]) => {
    const primary = skus[0]!;
    const image = skus.find(s => s.images && s.images.length > 0)?.images?.[0];
    const finishes = [...new Set(skus.map(s => s.finish).filter(Boolean) as string[])];
    return {
      groupKey: key,
      partNumber: primary.partNumber,
      brand: primary.brand,
      genericTitle: deriveGenericTitle(primary),
      finishes,
      image,
      skus,
    };
  });
}

interface WPFacets {
  brands: string[];
  boltPatterns: string[];
  finishes: string[];
  diameters: string[];
  widths: string[];
  years: string[];
}

type SearchMode = 'keyword' | 'vehicle';


function fmtPrice(v: number | string | null | undefined): string {
  if (v == null || v === '') return '—';
  const n = parseFloat(String(v));
  if (isNaN(n)) return String(v);
  return `$${n.toFixed(2)}`;
}

function WarehouseInventory({ rows }: { rows: WPInventoryRow[] }) {
  const [open, setOpen] = useState(false);

  if (rows.length === 0) return <span className="text-xs text-muted-foreground">No stock data</span>;

  const totalQty = rows.reduce((s, r) => s + (r.quantityAvailable ?? r.quantityOnHand ?? 0), 0);
  const inStock = totalQty > 0;

  return (
    <div>
      <button
        onClick={() => setOpen(v => !v)}
        className={cn(
          'flex items-center gap-1 text-xs px-1.5 py-0.5 rounded font-medium transition-colors',
          inStock ? 'bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25' : 'bg-red-500/15 text-red-400 hover:bg-red-500/25'
        )}
      >
        <Warehouse className="w-3 h-3" />
        {inStock ? `${totalQty} in stock` : 'Out of stock'}
        {rows.length > 1 && (open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
      </button>
      {open && rows.length > 1 && (
        <div className="mt-1.5 space-y-0.5 pl-1">
          {rows.map((r, i) => {
            const qty = r.quantityAvailable ?? r.quantityOnHand ?? 0;
            const label = r.warehouseName ?? r.warehouseCode ?? `Warehouse ${i + 1}`;
            return (
              <div key={i} className="flex items-center justify-between text-xs text-muted-foreground">
                <span className="truncate max-w-[120px]">{label}</span>
                <span className={qty > 0 ? 'text-emerald-400' : 'text-red-400'}>{qty}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ProductCard({
  product,
  selected,
  onToggle,
  pricing,
  inventoryRows,
}: {
  product: WPProduct;
  selected: boolean;
  onToggle: () => void;
  pricing?: WPPricing;
  inventoryRows: WPInventoryRow[];
}) {
  const [expanded, setExpanded] = useState(false);
  const [imgError, setImgError] = useState(false);
  const image = product.images?.[0];

  const price = pricing?.price ?? pricing?.mapPrice ?? product.mapPrice;
  const msrp = pricing?.msrp ?? product.msrp;
  const nip = pricing?.nip ?? pricing?.cost;

  const productType =
    product.category ??
    product.productCategory ??
    product.type ??
    product.productType ??
    product.productLine ??
    null;

  const specs = [
    product.sizeDesc && `Size: ${product.sizeDesc}`,
    product.finish && `Finish: ${product.finish}`,
    product.diameter && `Diameter: ${product.diameter}"`,
    product.width && `Width: ${product.width}"`,
    product.boltPattern && `Bolt Pattern: ${normalizeBp(product.boltPattern) || product.boltPattern}`,
    product.offset && `Offset: ${product.offset}mm`,
    product.centerbore && `CB: ${product.centerbore}mm`,
    product.backspacing && `Backspacing: ${product.backspacing}"`,
    product.loadRating && `Load: ${product.loadRating} lbs`,
    product.weight && `Weight: ${product.weight} lbs`,
    product.upc && `UPC: ${product.upc}`,
  ].filter(Boolean) as string[];

  return (
    <div
      className={cn(
        'rounded-xl border transition-all duration-200 overflow-hidden',
        selected
          ? 'border-primary/60 bg-primary/5 shadow-[0_0_12px_rgba(0,240,255,0.1)]'
          : 'border-border/60 bg-muted/20 hover:border-border'
      )}
    >
      <div className="flex items-start gap-3 p-4">
        <button
          onClick={onToggle}
          className={cn(
            'flex-shrink-0 w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center transition-all',
            selected
              ? 'border-primary bg-primary text-black'
              : 'border-border hover:border-border'
          )}
        >
          {selected && <Check className="w-3 h-3" />}
        </button>

        {image && !imgError ? (
          <img
            src={image}
            alt={product.description ?? product.sku}
            className="w-16 h-16 object-contain rounded-lg bg-muted/50 flex-shrink-0"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-16 h-16 rounded-lg bg-muted/50 flex items-center justify-center flex-shrink-0">
            <Package className="w-7 h-7 text-slate-600" />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground line-clamp-2 leading-snug">
            {deriveGenericTitle(product)}
          </p>
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
            <span className="text-xs text-muted-foreground">SKU: {product.sku}</span>
            {product.brand && <span className="text-xs text-muted-foreground">{product.brand}</span>}
            {product.partNumber && <span className="text-xs text-slate-600">Part: {product.partNumber}</span>}
            {productType && <span className="text-xs text-slate-600 italic">{productType}</span>}
          </div>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            {price != null && (
              <span className="text-sm font-bold text-primary">{fmtPrice(price)}</span>
            )}
            {msrp != null && (
              <span className="text-xs text-muted-foreground line-through">{fmtPrice(msrp)}</span>
            )}
            {nip != null && (
              <span className="text-xs text-emerald-400/80">Cost: {fmtPrice(nip)}</span>
            )}
            <WarehouseInventory rows={inventoryRows} />
          </div>
        </div>
      </div>

      {specs.length > 0 && (
        <div className="border-t border-border/50">
          <button
            onClick={() => setExpanded(v => !v)}
            className="w-full flex items-center justify-between px-4 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <span>Specifications ({specs.length})</span>
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
          {expanded && (
            <div className="px-4 pb-3 grid grid-cols-2 gap-x-4 gap-y-1">
              {specs.map(s => (
                <span key={s} className="text-xs text-muted-foreground">{s}</span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Grouped product card ───────────────────────────────────────────────────

function GroupedProductCard({
  group,
  selected,
  onToggleSkus,
  pricingMap,
  inventoryMap,
  fxRate = 1,
}: {
  group: WPProductGroup;
  selected: Set<string>;
  onToggleSkus: (skus: string[]) => void;
  pricingMap: Map<string, WPPricing>;
  inventoryMap: Map<string, WPInventoryRow[]>;
  fxRate?: number;
}) {
  const [imgError, setImgError] = useState(false);

  const allSel = group.skus.every(s => selected.has(s.sku));
  const someSel = group.skus.some(s => selected.has(s.sku));
  const partial = someSel && !allSel;

  // Price range across variants — msrp is the populated fallback when map_price is empty
  const rate = fxRate > 0 ? fxRate : 1;
  const toDisplay = (n: number) => Math.round(n * rate * 100) / 100;
  const prices = group.skus
    .map(s => {
      const pr = pricingMap.get(s.sku);
      const v = pr?.price ?? pr?.mapPrice ?? pr?.msrp ?? s.mapPrice ?? s.msrp;
      return v != null ? Number(v) : null;
    })
    .filter((v): v is number => v != null && isFinite(v) && v > 0);
  const minP = prices.length ? Math.min(...prices) : null;
  const maxP = prices.length ? Math.max(...prices) : null;
  const priceLabel = minP != null
    ? minP === maxP
      ? fmtPrice(toDisplay(minP))
      : `From ${fmtPrice(toDisplay(minP))}`
    : null;

  return (
    <div className={cn(
      'rounded-xl border transition-all duration-200 overflow-hidden',
      allSel
        ? 'border-primary/60 bg-primary/5 shadow-[0_0_12px_rgba(0,240,255,0.1)]'
        : partial
        ? 'border-primary/25 bg-primary/3'
        : 'border-border/60 bg-muted/20 hover:border-border'
    )}>
      {/* Header row */}
      <div className="flex items-start gap-3 p-4">
        {/* Group checkbox — selects / deselects all SKUs */}
        <button
          onClick={() => onToggleSkus(group.skus.map(s => s.sku))}
          className={cn(
            'flex-shrink-0 w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center transition-all',
            allSel
              ? 'border-primary bg-primary text-black'
              : partial
              ? 'border-primary/50 bg-primary/10'
              : 'border-border hover:border-border'
          )}
          title={allSel ? 'Deselect all variants' : 'Select all variants'}
        >
          {allSel && <Check className="w-3 h-3" />}
          {partial && <span className="w-2 h-0.5 bg-primary rounded-full block" />}
        </button>

        {/* Thumbnail */}
        {group.image && !imgError ? (
          <img
            src={group.image}
            alt={group.genericTitle}
            className="w-16 h-16 object-contain rounded-lg bg-muted/50 flex-shrink-0"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-16 h-16 rounded-lg bg-muted/50 flex items-center justify-center flex-shrink-0">
            <Package className="w-7 h-7 text-slate-600" />
          </div>
        )}

        {/* Meta */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-semibold text-foreground leading-snug">
              {group.genericTitle}
            </p>
            <span className="flex-shrink-0 px-1.5 py-0.5 rounded bg-muted/60 text-[10px] text-muted-foreground font-mono whitespace-nowrap">
              {group.skus.length} variant{group.skus.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
            {group.brand && <span className="text-xs text-muted-foreground">{group.brand}</span>}
            {group.partNumber && (
              <span className="text-xs text-slate-600 font-mono">{group.partNumber}</span>
            )}
          </div>
          {/* Aggregated Size / Finish / Offset — mirrors the Shopify description */}
          {(() => {
            const sizeSet = new Map<string, { d: number; w: number; label: string }>();
            for (const s of group.skus) {
              if (s.diameter && s.width) {
                const d = parseFloat(String(s.diameter));
                const w = parseFloat(String(s.width));
                if (!isNaN(d) && !isNaN(w)) {
                  const key = `${d}×${w}`;
                  if (!sizeSet.has(key))
                    sizeSet.set(key, { d, w, label: `${fmtNum(d)}" × ${fmtNum(w)}"` });
                }
              }
            }
            const allSizes = [...sizeSet.values()]
              .sort((a, b) => a.d - b.d || a.w - b.w)
              .map(v => v.label);

            const finishSet = new Set<string>();
            for (const s of group.skus) {
              const raw = ((s as any).csvFinish || s.finish || '').trim();
              const fin = raw.toLowerCase().replace(/\b\w/g, (c: string) => c.toUpperCase());
              if (fin) finishSet.add(fin);
            }
            const allFinishes = [...finishSet].sort();

            const offsetSet = new Set<string>();
            for (const s of group.skus) {
              if (s.offset != null && s.offset !== '') {
                const off = parseFloat(String(s.offset));
                if (!isNaN(off)) offsetSet.add(off >= 0 ? `+${off}` : String(off));
              }
            }
            const allOffsets = [...offsetSet].sort((a, b) => parseFloat(a) - parseFloat(b));

            const rows: [string, string][] = [];
            if (allSizes.length > 0)   rows.push(['Size',   allSizes.join(', ')]);
            if (allFinishes.length > 0) rows.push(['Finish', allFinishes.join(', ')]);
            if (allOffsets.length > 0)  rows.push(['Offset', allOffsets.map(o => `${o}mm`).join(', ')]);
            if (rows.length === 0) return null;

            return (
              <div className="mt-2 space-y-0.5">
                {rows.map(([label, value]) => (
                  <div key={label} className="flex gap-2 text-xs">
                    <span className="text-muted-foreground w-12 flex-shrink-0">{label}</span>
                    <span className="text-foreground leading-relaxed">{value}</span>
                  </div>
                ))}
              </div>
            );
          })()}
          {priceLabel && (
            <p className="text-sm font-bold text-primary mt-1">{priceLabel}</p>
          )}
        </div>
      </div>

      {/* Static model-level specifications (from primary SKU) */}
      {(() => {
        const p = group.skus[0]!;
        const str = (v: unknown) => v != null && v !== '' ? String(v).trim() : '';
        const rows: [string, string][] = [
          ['Model Code',           str(p.partNumber)],
          ['Style',                str(p.csvStyle || p.productLine)],
          ['Construction',         str(p.constructionType)],
          ['Lug Count',            str(p.lugCount)],
          ['Stud Pattern',         normalizeBp(p.boltPatternStandard || p.boltPattern) || str(p.boltPatternStandard || p.boltPattern)],
          ['Centre Bore',          p.centerbore != null && p.centerbore !== '' ? `${p.centerbore}mm` : ''],
          ['Backspacing',          p.backspacing != null && p.backspacing !== '' ? `${p.backspacing}"` : ''],
          ['Load Rating (Metric)', p.loadRatingMetric != null ? `${p.loadRatingMetric} kg` : ''],
          ['Load Rating (Std)',    p.loadRatingStandard != null ? `${p.loadRatingStandard} lbs` : str(p.loadRating)],
          ['Wheel Weight',         p.weight != null && p.weight !== '' ? `${p.weight} lbs` : ''],
          ['Lip Depth',            p.lipDepth != null ? `${p.lipDepth}"` : ''],
          ['Simple Colour',        str(p.abbreviatedFinishDesc)],
          ['TPMS Compatible',      p.tpmsCompatible != null ? (p.tpmsCompatible ? 'Yes' : 'No') : ''],
          ['Certifications',       str(p.certification)],
          ['Structural Warranty',  str(p.structuralWarranty)],
          ['Finish Warranty',      str(p.finishWarranty)],
          ['Cap Part No.',         str(p.capPartNo)],
          ['Rivet Part No.',       str(p.rivetPartNo)],
        ].filter(([, v]) => v !== '') as [string, string][];

        if (rows.length === 0) return null;
        return (
          <div className="border-t border-border/50 px-4 py-2.5">
            <div className="grid grid-cols-2 gap-x-6 gap-y-0.5">
              {rows.map(([label, value]) => (
                <div key={label} className="flex gap-1.5 text-[11px]">
                  <span className="text-muted-foreground shrink-0 min-w-0">{label}:</span>
                  <span className="text-foreground truncate" title={value}>{value}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

    </div>
  );
}

export function WheelPros() {
  const { token } = useAuthStore();
  const authOpts = useAuthHeaders();
  const { toast } = useToast();

  const [searchMode, setSearchMode] = useState<SearchMode>('keyword');
  const [params, setParams] = useState<SearchParams>({
    brand: '', sku: '', partNumber: '',
    finish: '', diameter: '', width: '', boltPattern: '',
    minOffset: '', maxOffset: '', availabilityType: 'ALL',
    year: '', make: '', model: '', trim: '',
  });
  const [page, setPage] = useState(1);
  const [searching, setSearching] = useState(false);
  const [products, setProducts] = useState<WPProduct[]>([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filterText, setFilterText] = useState('');
  const [selectingAllPages, setSelectingAllPages] = useState(false);
  const [allPagesSelected, setAllPagesSelected] = useState(false);

  // pricingMap: sku → pricing object
  const [pricingMap, setPricingMap] = useState<Map<string, WPPricing>>(new Map());
  // inventoryMap: sku → WPInventoryRow[] (multiple warehouse rows per SKU)
  const [inventoryMap, setInventoryMap] = useState<Map<string, WPInventoryRow[]>>(new Map());
  const [enriching, setEnriching] = useState(false);

  const [storeId, setStoreId] = useState('');
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<{ current: number; total: number } | null>(null);
  const [importStatus, setImportStatus] = useState<string>('');
  const [importResult, setImportResult] = useState<{
    successCount: number;
    skipCount: number;
    noPriceSkipCount: number;
    errorCount: number;
    errors: { sku: string; error: string }[];
    inputDuplicateCount: number;
    droppedVariantCount: number;
  } | null>(null);
  /** jobId returned by the server when an SSE import starts — used for polling after a disconnect */
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  /** true when the SSE stream dropped mid-import and we are polling for status */
  const [importConnectionLost, setImportConnectionLost] = useState(false);
  const importDoneRef = useRef(false);

  const [duplicateHandling, setDuplicateHandling] = useState<'skip' | 'overwrite' | 'create'>('skip');
  const [productStatus, setProductStatus] = useState<'active' | 'draft'>('active');
  const [productStructure, setProductStructure] = useState<'grouped' | 'individual'>('grouped');
  const [hideNoImages, setHideNoImages] = useState(true);
  const [productTemplate, setProductTemplate] = useState('');
  const [productTemplates, setProductTemplates] = useState<{ label: string; value: string }[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [trackQuantity, setTrackQuantity] = useState(true);
  const [fxRate, setFxRate] = useState(1.60);

  const [repricing, setRepricing] = useState(false);
  const [repriceMode, setRepriceMode] = useState<'rate' | 'flat'>('rate');
  const [repriceRate, setRepriceRate] = useState(1.60);
  const [repriceFlat, setRepriceFlat] = useState(50);
  const [repriceProgress, setRepriceProgress] = useState<{ current: number; total: number; status?: string } | null>(null);
  const [repriceResult, setRepriceResult] = useState<{
    updatedVariants: number;
    unchangedVariants: number;
    errorCount: number;
    errors: { sku: string; error: string }[];
  } | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [repricePreview, setRepricePreview] = useState<Array<{
    sku: string;
    productTitle: string;
    variantTitle: string;
    currentPrice: number;
    currentCompareAt: number;
    newPrice: number;
    newCompareAt: number;
  }> | null>(null);

  const [resetRate, setResetRate] = useState(1.60);
  const [resetting, setResetting] = useState(false);
  const [resetPreviewing, setResetPreviewing] = useState(false);
  const [resetProgress, setResetProgress] = useState<{ current: number; total: number; status?: string } | null>(null);
  const [resetResult, setResetResult] = useState<{
    updatedVariants: number;
    unchangedVariants: number;
    errorCount: number;
    errors: { sku: string; error: string }[];
  } | null>(null);
  const [resetPreview, setResetPreview] = useState<Array<{
    sku: string;
    productTitle: string;
    variantTitle: string;
    currentPrice: number;
    currentCompareAt: number;
    newPrice: number;
    newCompareAt: number;
  }> | null>(null);

  const [costRate, setCostRate] = useState(1.60);
  const [costUpdating, setCostUpdating] = useState(false);
  const [costPreviewing, setCostPreviewing] = useState(false);
  const [costProgress, setCostProgress] = useState<{ current: number; total: number; status?: string } | null>(null);
  const [costResult, setCostResult] = useState<{
    updatedVariants: number;
    unchangedVariants: number;
    skippedNoNip: number;
    errorCount: number;
  } | null>(null);
  const [costPreview, setCostPreview] = useState<Array<{
    sku: string;
    productTitle: string;
    variantTitle: string;
    nipUsd: number;
    newCostAud: number;
  }> | null>(null);

  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);

  const [backfilling, setBackfilling] = useState(false);
  const [backfillProgress, setBackfillProgress] = useState<{ current: number; total: number; status?: string } | null>(null);
  const [backfillResult, setBackfillResult] = useState<{
    linked: number;
    alreadyLinked: number;
    noImage: number;
    errorCount: number;
    errors: { sku: string; error: string }[];
  } | null>(null);

  const queryClient = useQueryClient();

  const PAGE_SIZE = 50;

  const ratesInitialised = React.useRef(false);

  const { data: status } = useQuery<{ configured: boolean; connected?: boolean; error?: string; salesOrg?: string }>({
    queryKey: ['wheelpros-status'],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/wheelpros/status`, { headers: authOpts.headers });
      return res.json();
    },
    enabled: !!token,
    staleTime: 60_000,
  });

  const { data: stores = [] } = useQuery<ShopifyStore[]>({
    queryKey: ['stores'],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/stores`, { headers: authOpts.headers });
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
    enabled: !!token,
  });

  // Load filter facets from WP API — brands, finishes, diameters, widths, bolt patterns
  const { data: facets } = useQuery<WPFacets>({
    queryKey: ['wp-facets'],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/wheelpros/facets`, { headers: authOpts.headers });
      if (!res.ok) return { brands: [], boltPatterns: [], finishes: [], diameters: [], widths: [], years: [] };
      return res.json();
    },
    enabled: !!token && !!status?.configured,
    staleTime: 10 * 60 * 1000, // 10 min — WP catalog metadata is stable within a session
    refetchOnWindowFocus: false,
  });

  type RepriceChange = {
    sku: string; productTitle: string; variantTitle: string;
    oldPrice: number; oldCompareAt: number; newPrice: number; newCompareAt: number;
  };
  type RepriceLogEntry = {
    id: string; fxRate: number; updatedVariants: number; unchangedVariants: number;
    errorCount: number; changes: RepriceChange[]; errors: { sku: string; error: string }[]; ranAt: string;
  };

  const { data: repriceLogs = [], refetch: refetchRepriceLogs } = useQuery<RepriceLogEntry[]>({
    queryKey: ['reprice-logs', storeId],
    queryFn: async () => {
      if (!storeId) return [];
      const res = await fetch(`${BASE}/api/wheelpros/reprice-logs?storeId=${storeId}`, { headers: authOpts.headers });
      if (!res.ok) return [];
      const d = await res.json();
      return d.logs ?? [];
    },
    enabled: !!token && !!storeId,
    staleTime: 30_000,
  });

  React.useEffect(() => {
    if (stores.length === 1 && !storeId) setStoreId(stores[0].id);
  }, [stores, storeId]);

  // Bust the import log page's cache after a successful import so it reflects new data
  React.useEffect(() => {
    if (importResult && storeId) {
      queryClient.invalidateQueries({ queryKey: ['wp-import-log', storeId] });
    }
  }, [importResult, storeId]);

  // ── Import status polling ──────────────────────────────────────────────────
  // Fires when the SSE stream drops mid-import. Polls /import-status every 3 s
  // to keep the progress counter alive and detect completion.

  useEffect(() => {
    if (!importConnectionLost || !storeId || !token) return;

    let cancelled = false;

    const poll = async () => {
      try {
        const res = await fetch(`${BASE}/api/wheelpros/import-status?storeId=${storeId}`, { headers: authOpts.headers });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (!data.active || !data.job) return;
        const job = data.job;

        // Update progress from polled state
        if (job.total > 0) {
          setImportProgress({ current: job.current, total: job.total });
          setImportStatus('');
        } else if (job.statusMessage) {
          setImportStatus(job.statusMessage);
        }

        if (job.phase === 'done') {
          importDoneRef.current = true;
          setImportResult({
            successCount: job.successCount ?? 0,
            skipCount: job.skipCount ?? 0,
            noPriceSkipCount: job.noPriceSkipCount ?? 0,
            errorCount: job.errorCount ?? 0,
            errors: job.errors ?? [],
            inputDuplicateCount: job.inputDuplicateCount ?? 0,
            droppedVariantCount: job.droppedVariantCount ?? 0,
          });
          setSelected(new Set());
          setImporting(false);
          setImportProgress(null);
          setImportStatus('');
          setImportConnectionLost(false);
        } else if (job.phase === 'error') {
          setImporting(false);
          setImportProgress(null);
          setImportStatus('');
          setImportConnectionLost(false);
          toast({ title: 'Import failed', description: job.statusMessage ?? 'An error occurred.', variant: 'destructive' });
        }
      } catch { /* network error — keep polling */ }
    };

    // Poll immediately then every 3 seconds
    poll();
    const interval = setInterval(() => { if (!cancelled) poll(); }, 3000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [importConnectionLost, storeId, token, authOpts.headers]);

  // ── Restore in-progress import on mount / store change ────────────────────
  // If the user navigates back to this page while an import is still running
  // (job is in the registry on the server), restore the progress panel.

  useEffect(() => {
    if (!storeId || !token || importing) return;
    let cancelled = false;

    fetch(`${BASE}/api/wheelpros/import-status?storeId=${storeId}`, { headers: authOpts.headers })
      .then(r => r.ok ? r.json() : Promise.resolve({ active: false }))
      .then(data => {
        if (cancelled || !data.active || !data.job) return;
        const job = data.job;
        if (job.phase !== 'importing' && job.phase !== 'preflight') return;
        // Job is still running — restore the progress panel
        setActiveJobId(job.jobId);
        setImporting(true);
        setImportConnectionLost(true);
        if (job.total > 0) {
          setImportProgress({ current: job.current, total: job.total });
        } else {
          setImportStatus(job.statusMessage || 'Import running…');
        }
      })
      .catch(() => { /* non-fatal */ });

    return () => { cancelled = true; };
  // Only run once per store selection, not on every render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, token]);

  const DEFAULT_TEMPLATES = [{ label: 'Default template', value: '' }];

  // Fetch available product templates from the selected store's published theme.
  // Resets the selected template to default whenever the store changes.
  React.useEffect(() => {
    if (!storeId || !token) {
      setProductTemplates([]);
      setProductTemplate('');
      return;
    }
    // Pre-seed with the default immediately so the selector is always visible
    setProductTemplates(DEFAULT_TEMPLATES);
    setProductTemplate('');
    let cancelled = false;
    setLoadingTemplates(true);
    fetch(`${BASE}/api/wheelpros/product-templates?storeId=${storeId}`, { headers: authOpts.headers })
      .then(r => r.ok ? r.json() : Promise.resolve({ templates: DEFAULT_TEMPLATES }))
      .then(data => {
        if (!cancelled) setProductTemplates([...DEFAULT_TEMPLATES, ...(data.templates ?? [])]);
      })
      .catch(() => {
        if (!cancelled) setProductTemplates(DEFAULT_TEMPLATES);
      })
      .finally(() => {
        if (!cancelled) setLoadingTemplates(false);
      });
    return () => { cancelled = true; };
  }, [storeId, token]);

  const { data: years = [] } = useQuery<string[]>({
    queryKey: ['wp-years'],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/wheelpros/vehicles/years`, { headers: authOpts.headers });
      const d = await res.json();
      return d.years ?? [];
    },
    enabled: !!token && searchMode === 'vehicle' && !!status?.connected,
    staleTime: 3_600_000,
  });

  const { data: makes = [] } = useQuery<string[]>({
    queryKey: ['wp-makes', params.year],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/wheelpros/vehicles/makes?year=${params.year}`, { headers: authOpts.headers });
      const d = await res.json();
      return d.makes ?? [];
    },
    enabled: !!token && !!params.year && searchMode === 'vehicle',
    staleTime: 3_600_000,
  });

  const { data: models = [] } = useQuery<string[]>({
    queryKey: ['wp-models', params.year, params.make],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/wheelpros/vehicles/models?year=${params.year}&make=${encodeURIComponent(params.make)}`, { headers: authOpts.headers });
      const d = await res.json();
      return d.models ?? [];
    },
    enabled: !!token && !!params.year && !!params.make && searchMode === 'vehicle',
    staleTime: 0,
  });

  // Client-side guard: strictly filter to models whose name begins with the
  // selected make followed by a word-boundary character (space, dash, slash).
  // This removes sibling-brand models that WheelPros sometimes returns under a
  // shared make code (e.g. Gas Gas models appearing under the KTM make).
  // The backend already applies the same filter; this is a second safety net.
  const filteredModels = React.useMemo(() => {
    if (!params.make || models.length === 0) return models;
    const makeNorm = params.make.trim().toLowerCase();
    const matched = models.filter(m => {
      const mNorm = m.trim().toLowerCase();
      if (mNorm === makeNorm) return true;
      const sep = mNorm.charAt(makeNorm.length);
      return mNorm.startsWith(makeNorm) && (sep === ' ' || sep === '-' || sep === '/' || sep === '_');
    });
    // If the strict filter yields results, use them exclusively — no fallback to
    // the full list, which would re-introduce cross-brand pollution.
    // If zero match (model names don't carry the make as a prefix in this catalog),
    // pass the raw list through unchanged.
    return matched;
  }, [models, params.make]);

  const { data: trims = [] } = useQuery<string[]>({
    queryKey: ['wp-trims', params.year, params.make, params.model],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/wheelpros/vehicles/trims?year=${params.year}&make=${encodeURIComponent(params.make)}&model=${encodeURIComponent(params.model)}`, { headers: authOpts.headers });
      const d = await res.json();
      return d.trims ?? [];
    },
    enabled: !!token && !!params.year && !!params.make && !!params.model && searchMode === 'vehicle',
    staleTime: 3_600_000,
  });

  const enrichProducts = async (skus: string[]) => {
    if (skus.length === 0) return;
    setEnriching(true);
    try {
      const [pricingRes, inventoryRes] = await Promise.all([
        fetch(`${BASE}/api/wheelpros/pricing?skus=${skus.join(',')}`, { headers: authOpts.headers }).then(r => r.json()),
        fetch(`${BASE}/api/wheelpros/inventory?skus=${skus.join(',')}`, { headers: authOpts.headers }).then(r => r.json()),
      ]);

      setPricingMap(prev => {
        const next = new Map(prev);
        (pricingRes.pricing ?? [] as WPPricing[]).forEach((p: WPPricing) => next.set(p.sku, p));
        return next;
      });

      // Inventory response is an array of rows; group by SKU to get warehouse-level detail.
      // Build a per-SKU accumulator first so we can replace the seeded inline rows entirely
      // when real warehouse data arrives.
      setInventoryMap(prev => {
        const next = new Map(prev);
        const realRows = new Map<string, WPInventoryRow[]>();
        for (const row of (inventoryRes.inventory ?? []) as WPInventoryRow[]) {
          const bucket = realRows.get(row.sku) ?? [];
          bucket.push(row);
          realRows.set(row.sku, bucket);
        }
        // Replace seeded inline rows with actual warehouse rows for any SKU that has them.
        // For SKUs with no real data the seeded inline rows remain as fallback.
        for (const [sku, rows] of realRows) {
          if (rows.length > 0) next.set(sku, rows);
        }
        return next;
      });
    } catch {
      // non-fatal — inventory/pricing enrichment best-effort
    } finally {
      setEnriching(false);
    }
  };

  // overrideSku: when the results filter bar is used to search all pages,
  // the typed text is passed here so it is sent to the API without mutating
  // the main params state (which would change the search form fields).
  // forceImagesOnly: explicit override for the imagesOnly param — used when
  // the toggle is clicked so the new value takes effect before state updates.
  const doSearch = useCallback(async (pageNum = 1, overrideSku?: string, forceImagesOnly?: boolean) => {
    setSearching(true);
    setSearchError(null);
    setImportResult(null);
    if (pageNum === 1) {
      setPricingMap(new Map());
      setInventoryMap(new Map());
    }

    const qp = new URLSearchParams({ page: String(pageNum), pageSize: String(PAGE_SIZE) });
    const useImagesOnly = forceImagesOnly !== undefined ? forceImagesOnly : hideNoImages;
    if (useImagesOnly) qp.set('imagesOnly', 'true');
    if (params.availabilityType && params.availabilityType !== 'ALL') qp.set('availabilityType', params.availabilityType);
    if (searchMode === 'keyword') {
      if (params.brand) qp.set('brand', params.brand);
      // overrideSku replaces both sku and partNumber when coming from the filter bar
      const skuToUse = overrideSku ?? params.sku.trim();
      if (skuToUse) qp.set('sku', skuToUse);
      if (!overrideSku && params.partNumber.trim()) qp.set('partNumber', params.partNumber.trim());
      if (params.finish) qp.set('finish', params.finish);
      if (params.diameter) qp.set('diameter', params.diameter);
      if (params.width) qp.set('width', params.width);
      if (params.boltPattern) qp.set('boltPattern', params.boltPattern);
      if (params.minOffset.trim()) qp.set('minOffset', params.minOffset.trim());
      if (params.maxOffset.trim()) qp.set('maxOffset', params.maxOffset.trim());
    } else {
      if (params.year) qp.set('year', params.year);
      if (params.make) qp.set('make', params.make);
      if (params.model) qp.set('model', params.model);
      if (params.trim) qp.set('trim', params.trim);
    }

    try {
      const res = await fetch(`${BASE}/api/wheelpros/products?${qp}`, { headers: authOpts.headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? `Error ${res.status}`);
      const prods: WPProduct[] = data.products ?? [];
      setProducts(prods);
      setTotalProducts(data.total ?? prods.length);
      setPage(pageNum);
      // Clear selection and filter when results change
      setSelected(new Set());
      setFilterText('');
      setAllPagesSelected(false);

      // Immediately seed inventory map from inline localStock/globalStock so stock
      // is visible as soon as search results arrive — no second round-trip needed.
      // enrichProducts will overwrite with per-warehouse rows when the inventory
      // endpoint returns richer data.
      // Always set an entry for every product (even if both stocks are 0) so
      // the card shows "Out of stock" rather than "No stock data" while the
      // enrichment call is in flight.
      setInventoryMap(prev => {
        const next = new Map(prev);
        for (const p of prods) {
          if (!next.has(p.sku)) {
            const rows: WPInventoryRow[] = [];
            if ((p.localStock ?? 0) > 0) {
              rows.push({ sku: p.sku, warehouseCode: 'LOCAL', warehouseName: 'Local', quantityAvailable: p.localStock });
            }
            if ((p.globalStock ?? 0) > 0) {
              rows.push({ sku: p.sku, warehouseCode: 'GLOBAL', warehouseName: 'Global', quantityAvailable: p.globalStock });
            }
            // Guarantee at least one row so "No stock data" never appears
            if (rows.length === 0) {
              rows.push({ sku: p.sku, quantityAvailable: 0 });
            }
            next.set(p.sku, rows);
          }
        }
        return next;
      });

      await enrichProducts(prods.map(p => p.sku));
    } catch (err: any) {
      setSearchError(err.message);
      setProducts([]);
    } finally {
      setSearching(false);
    }
  }, [params, searchMode, authOpts.headers, hideNoImages]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    doSearch(1);
  };

  const toggleSelect = (sku: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(sku) ? next.delete(sku) : next.add(sku);
      return next;
    });
  };

  /** Toggle one or more SKUs: if ALL are selected, deselect them; otherwise select all. */
  const toggleSkus = (skus: string[]) => {
    setSelected(prev => {
      const next = new Set(prev);
      const allIn = skus.every(s => next.has(s));
      if (allIn) skus.forEach(s => next.delete(s));
      else skus.forEach(s => next.add(s));
      return next;
    });
  };

  const runImport = async () => {
    if (!storeId) {
      toast({ title: 'Select a store', description: 'Choose a destination Shopify store first.', variant: 'destructive' });
      return;
    }
    if (selected.size === 0) {
      toast({ title: 'No products selected', description: 'Select at least one product to import.', variant: 'destructive' });
      return;
    }

    setImporting(true);
    setImportProgress(null);
    setImportResult(null);
    setImportConnectionLost(false);
    setActiveJobId(null);
    importDoneRef.current = false;
    // Local flag so the finally block knows whether the connection was lost
    // (can't rely on state in a closure — state updates are async).
    let connectionDropped = false;

    try {
      // Deduplicate SKUs before sending — cross-page select-all or concurrent
      // selections can theoretically add the same SKU twice. The Set already
      // deduplicates, but spread to array and re-dedup for safety.
      const selectedSkus = [...new Set(Array.from(selected))];
      // Build a sku→product map from the current search results so the server
      // can group variants without calling the detail endpoint (which 500s for
      // some brands like KMC).
      const productData: Record<string, WPProduct> = {};
      for (const p of products) {
        if (selected.has(p.sku)) productData[p.sku] = p;
      }

      const res = await fetch(`${BASE}/api/wheelpros/import`, {
        method: 'POST',
        headers: { ...authOpts.headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          storeId,
          skus: selectedSkus,
          duplicateHandling,
          productStatus,
          productStructure,
          productData,
          trackQuantity,
          fxRate,
          ...(productTemplate !== '' ? { templateSuffix: productTemplate } : {}),
        }),
      });

      if (!res.ok || !res.body) {
        const d = await res.json().catch(() => ({ error: `Error ${res.status}` }));
        throw new Error(d.error ?? 'Import failed');
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const lines = buf.split('\n');
          buf = lines.pop() ?? '';
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const ev = JSON.parse(line.slice(6));
              if (ev.type === 'start') {
                setActiveJobId(ev.jobId ?? null);
              }
              if (ev.type === 'status') setImportStatus(ev.message ?? '');
              if (ev.type === 'progress') { setImportProgress({ current: ev.current, total: ev.total }); setImportStatus(''); }
              if (ev.type === 'done') {
                importDoneRef.current = true;
                setImportResult({
                  successCount: ev.successCount,
                  skipCount: ev.skipCount,
                  noPriceSkipCount: ev.noPriceSkipCount ?? 0,
                  errorCount: ev.errorCount,
                  errors: ev.errors ?? [],
                  inputDuplicateCount: ev.inputDuplicateCount ?? 0,
                  droppedVariantCount: ev.droppedVariantCount ?? 0,
                });
                setSelected(new Set());
              }
            } catch { }
          }
        }
      } catch {
        // SSE connection dropped — switch to polling if the job hasn't finished
      }

      // If the stream ended without a "done" event, the connection dropped mid-import.
      // Keep `importing = true` and switch to polling via the useEffect below.
      if (!importDoneRef.current) {
        connectionDropped = true;
        setImportConnectionLost(true);
        return; // don't clear importing state — the polling effect takes over
      }
    } catch (err: any) {
      toast({ title: 'Import failed', description: err.message, variant: 'destructive' });
    } finally {
      // Don't clear state when the connection dropped — the polling useEffect
      // will clean up once the job finishes on the server.
      if (!connectionDropped) {
        setImporting(false);
        setImportProgress(null);
        setImportStatus('');
        setImportConnectionLost(false);
      }
    }
  };

  const runReprice = async () => {
    if (!storeId) {
      toast({ title: 'Select a store', description: 'Choose a Shopify store first.', variant: 'destructive' });
      return;
    }

    setRepricing(true);
    setRepriceProgress(null);
    setRepriceResult(null);
    setRepricePreview(null);

    try {
      const res = await fetch(`${BASE}/api/wheelpros/reprice`, {
        method: 'POST',
        headers: { ...authOpts.headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(repriceMode === 'flat'
          ? { storeId, flatIncrement: repriceFlat }
          : { storeId, fxRate: repriceRate }),
      });

      if (!res.ok || !res.body) {
        const d = await res.json().catch(() => ({ error: `Error ${res.status}` }));
        throw new Error(d.error ?? 'Reprice failed');
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const ev = JSON.parse(line.slice(6));
            if (ev.type === 'status') {
              setRepriceProgress(prev => ({ current: prev?.current ?? 0, total: prev?.total ?? 0, status: ev.message }));
            }
            if (ev.type === 'progress') {
              setRepriceProgress(prev => ({ current: ev.current, total: ev.total, status: prev?.status }));
            }
            if (ev.type === 'error') {
              toast({ title: 'Re-price failed', description: ev.message ?? 'An unexpected error occurred.', variant: 'destructive' });
            }
            if (ev.type === 'done') {
              setRepriceResult({
                updatedVariants: ev.updatedVariants ?? 0,
                unchangedVariants: ev.unchangedVariants ?? 0,
                errorCount: ev.errorCount ?? 0,
                errors: ev.errors ?? [],
              });
              if ((ev.updatedVariants ?? 0) > 0) {
                refetchRepriceLogs();
              }
            }
          } catch { }
        }
      }
    } catch (err: any) {
      toast({ title: 'Re-price failed', description: err.message, variant: 'destructive' });
    } finally {
      setRepricing(false);
      setRepriceProgress(null);
    }
  };

  const runPreview = async () => {
    if (!storeId) {
      toast({ title: 'Select a store', description: 'Choose a Shopify store first.', variant: 'destructive' });
      return;
    }
    setPreviewing(true);
    setRepricePreview(null);
    try {
      const res = await fetch(`${BASE}/api/wheelpros/reprice`, {
        method: 'POST',
        headers: { ...authOpts.headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(repriceMode === 'flat'
          ? { storeId, flatIncrement: repriceFlat, dryRun: true }
          : { storeId, fxRate: repriceRate, dryRun: true }),
      });
      if (!res.ok || !res.body) {
        const d = await res.json().catch(() => ({ error: `Error ${res.status}` }));
        throw new Error(d.error ?? 'Preview failed');
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const ev = JSON.parse(line.slice(6));
            if (ev.type === 'error') {
              toast({ title: 'Preview failed', description: ev.message ?? 'An unexpected error occurred.', variant: 'destructive' });
            }
            if (ev.type === 'done') {
              setRepricePreview(ev.preview ?? []);
            }
          } catch { }
        }
      }
    } catch (err: any) {
      toast({ title: 'Preview failed', description: err.message, variant: 'destructive' });
    } finally {
      setPreviewing(false);
    }
  };

  const streamSse = async (
    url: string,
    body: object,
    onStatus: (msg: string) => void,
    onProgress: (current: number, total: number) => void,
  ): Promise<Record<string, any>> => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { ...authOpts.headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok || !res.body) {
      const d = await res.json().catch(() => ({ error: `Error ${res.status}` }));
      throw new Error(d.error ?? 'Request failed');
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    let donePayload: Record<string, any> = {};
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const lines = buf.split('\n');
      buf = lines.pop() ?? '';
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        try {
          const ev = JSON.parse(line.slice(6));
          if (ev.type === 'status')   onStatus(ev.message ?? '');
          if (ev.type === 'progress') onProgress(ev.current, ev.total);
          if (ev.type === 'error')    throw new Error(ev.message ?? 'Stream error');
          if (ev.type === 'done')     donePayload = ev;
        } catch (e: any) {
          if (e.message && e.message !== 'Stream error') throw e;
        }
      }
    }
    return donePayload;
  };

  const runResetPreview = async () => {
    if (!storeId) {
      toast({ title: 'Select a store', description: 'Choose a Shopify store first.', variant: 'destructive' });
      return;
    }
    setResetPreviewing(true);
    setResetPreview(null);
    setResetProgress(null);
    try {
      const done = await streamSse(
        `${BASE}/api/wheelpros/reset-to-api-price`,
        { storeId, fxRate: resetRate, dryRun: true },
        msg => setResetProgress(prev => ({ current: prev?.current ?? 0, total: prev?.total ?? 0, status: msg })),
        (current, total) => setResetProgress(prev => ({ current, total, status: prev?.status })),
      );
      setResetPreview(done.preview ?? []);
    } catch (err: any) {
      toast({ title: 'Preview failed', description: err.message, variant: 'destructive' });
    } finally {
      setResetPreviewing(false);
      setResetProgress(null);
    }
  };

  const runReset = async () => {
    if (!storeId) {
      toast({ title: 'Select a store', description: 'Choose a Shopify store first.', variant: 'destructive' });
      return;
    }
    setResetting(true);
    setResetProgress(null);
    setResetResult(null);
    setResetPreview(null);
    try {
      const done = await streamSse(
        `${BASE}/api/wheelpros/reset-to-api-price`,
        { storeId, fxRate: resetRate },
        msg => setResetProgress(prev => ({ current: prev?.current ?? 0, total: prev?.total ?? 0, status: msg })),
        (current, total) => setResetProgress(prev => ({ current, total, status: prev?.status })),
      );
      setResetResult({
        updatedVariants:   done.updatedVariants   ?? 0,
        unchangedVariants: done.unchangedVariants ?? 0,
        errorCount:        done.errorCount        ?? 0,
        errors:            done.errors            ?? [],
      });
    } catch (err: any) {
      toast({ title: 'Reset failed', description: err.message, variant: 'destructive' });
    } finally {
      setResetting(false);
      setResetProgress(null);
    }
  };

  const runCostPreview = async () => {
    if (!storeId) {
      toast({ title: 'Select a store', description: 'Choose a Shopify store first.', variant: 'destructive' });
      return;
    }
    setCostPreviewing(true);
    setCostPreview(null);
    setCostProgress(null);
    try {
      const done = await streamSse(
        `${BASE}/api/wheelpros/update-cost-price`,
        { storeId, fxRate: costRate, dryRun: true },
        msg => setCostProgress(prev => ({ current: prev?.current ?? 0, total: prev?.total ?? 0, status: msg })),
        (current, total) => setCostProgress(prev => ({ current, total, status: prev?.status })),
      );
      setCostPreview(done.preview ?? []);
    } catch (err: any) {
      toast({ title: 'Preview failed', description: err.message, variant: 'destructive' });
    } finally {
      setCostPreviewing(false);
      setCostProgress(null);
    }
  };

  const runCostUpdate = async () => {
    if (!storeId) {
      toast({ title: 'Select a store', description: 'Choose a Shopify store first.', variant: 'destructive' });
      return;
    }
    setCostUpdating(true);
    setCostProgress(null);
    setCostResult(null);
    setCostPreview(null);
    try {
      const done = await streamSse(
        `${BASE}/api/wheelpros/update-cost-price`,
        { storeId, fxRate: costRate },
        msg => setCostProgress(prev => ({ current: prev?.current ?? 0, total: prev?.total ?? 0, status: msg })),
        (current, total) => setCostProgress(prev => ({ current, total, status: prev?.status })),
      );
      setCostResult({
        updatedVariants:   done.updatedVariants   ?? 0,
        unchangedVariants: done.unchangedVariants ?? 0,
        skippedNoNip:      done.skippedNoNip      ?? 0,
        errorCount:        done.errorCount        ?? 0,
      });
    } catch (err: any) {
      toast({ title: 'Cost update failed', description: err.message, variant: 'destructive' });
    } finally {
      setCostUpdating(false);
      setCostProgress(null);
    }
  };

  const runBackfill = async () => {
    if (!storeId) {
      toast({ title: 'Select a store', description: 'Choose a Shopify store first.', variant: 'destructive' });
      return;
    }

    setBackfilling(true);
    setBackfillProgress(null);
    setBackfillResult(null);

    try {
      const res = await fetch(`${BASE}/api/wheelpros/backfill-images`, {
        method: 'POST',
        headers: { ...authOpts.headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ storeId }),
      });

      if (!res.ok || !res.body) {
        const d = await res.json().catch(() => ({ error: `Error ${res.status}` }));
        throw new Error(d.error ?? 'Backfill failed');
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const ev = JSON.parse(line.slice(6));
            if (ev.type === 'status') {
              setBackfillProgress(prev => ({ current: prev?.current ?? 0, total: prev?.total ?? 0, status: ev.message }));
            }
            if (ev.type === 'progress') {
              setBackfillProgress(prev => ({ current: ev.current, total: ev.total, status: prev?.status }));
            }
            if (ev.type === 'error') {
              toast({ title: 'Backfill failed', description: ev.message ?? 'An unexpected error occurred.', variant: 'destructive' });
            }
            if (ev.type === 'done') {
              setBackfillResult({
                linked: ev.linked ?? 0,
                alreadyLinked: ev.alreadyLinked ?? 0,
                noImage: ev.noImage ?? 0,
                errorCount: ev.errorCount ?? 0,
                errors: ev.errors ?? [],
              });
            }
          } catch { }
        }
      }
    } catch (err: any) {
      toast({ title: 'Backfill failed', description: err.message, variant: 'destructive' });
    } finally {
      setBackfilling(false);
      setBackfillProgress(null);
    }
  };

  const setParam = (key: keyof SearchParams, val: string) => {
    setParams(prev => {
      const next = { ...prev, [key]: val };
      if (key === 'year') { next.make = ''; next.model = ''; next.trim = ''; }
      if (key === 'make') { next.model = ''; next.trim = ''; }
      if (key === 'model') { next.trim = ''; }
      return next;
    });
  };

  const totalPages = Math.ceil(totalProducts / PAGE_SIZE);
  const configured = status?.configured;
  const connected = status?.connected;

  // Map salesOrg to ISO currency code — mirrors the backend helper
  const salesOrgCurrencyMap: Record<string, string> = {
    "1000": "USD", "1500": "USD", "4000": "CAD",
    "5000": "AUD", "6000": "GBP", "7000": "EUR",
  };
  const wpCurrency = salesOrgCurrencyMap[status?.salesOrg ?? ""] ?? "USD";

  // Once the account currency is known, initialise all rate inputs.
  // For non-USD accounts the API already returns prices in the account currency,
  // so the multiplier defaults to 1.0 (no conversion needed).
  // For USD accounts the conventional AUD conversion of 1.60 is kept.
  React.useEffect(() => {
    if (!status?.salesOrg || ratesInitialised.current) return;
    ratesInitialised.current = true;
    const defaultRate = wpCurrency === "USD" ? 1.60 : 1.0;
    setFxRate(defaultRate);
    setRepriceRate(defaultRate);
    setResetRate(1.0);   // API reprice always fetches in account currency
    setCostRate(1.0);    // cost (NIP) always fetched in account currency
  }, [status?.salesOrg, wpCurrency]);

  const filterLower = filterText.trim().toLowerCase();
  const displayedProducts = filterLower
    ? products.filter(p =>
        (p.sku ?? '').toLowerCase().includes(filterLower) ||
        (p.description ?? '').toLowerCase().includes(filterLower) ||
        (p.brand ?? '').toLowerCase().includes(filterLower) ||
        (p.finish ?? '').toLowerCase().includes(filterLower) ||
        (p.partNumber ?? '').toLowerCase().includes(filterLower) ||
        (p.productLine ?? '').toLowerCase().includes(filterLower)
      )
    : products;
  // Count products on this page that have no images — shown as a preview
  // on the toggle badge when the filter is OFF so the user knows what they'd hide.
  const noImageCount = products.filter(p => !(p.images && p.images.length > 0)).length;

  const groupedDisplayed = groupProducts(displayedProducts);
  const allGroupsSelected =
    groupedDisplayed.length > 0 &&
    groupedDisplayed.every(g => g.skus.every(s => selected.has(s.sku)));

  const toggleAll = () => {
    const allSkus = displayedProducts.map(p => p.sku);
    setSelected(prev => {
      const next = new Set(prev);
      if (allGroupsSelected) {
        allSkus.forEach(s => next.delete(s));
        setAllPagesSelected(false);
      } else {
        allSkus.forEach(s => next.add(s));
      }
      return next;
    });
  };

  /** Fetch every matching SKU across all pages and select them all at once. */
  const selectAllPages = useCallback(async () => {
    setSelectingAllPages(true);
    try {
      const qp = new URLSearchParams({ skusOnly: 'true' });
      if (hideNoImages) qp.set('imagesOnly', 'true');
      if (params.availabilityType && params.availabilityType !== 'ALL') qp.set('availabilityType', params.availabilityType);
      if (searchMode === 'keyword') {
        if (params.brand) qp.set('brand', params.brand);
        if (params.sku.trim()) qp.set('sku', params.sku.trim());
        if (params.partNumber.trim()) qp.set('partNumber', params.partNumber.trim());
        if (params.finish) qp.set('finish', params.finish);
        if (params.diameter) qp.set('diameter', params.diameter);
        if (params.width) qp.set('width', params.width);
        if (params.boltPattern) qp.set('boltPattern', params.boltPattern);
        if (params.minOffset.trim()) qp.set('minOffset', params.minOffset.trim());
        if (params.maxOffset.trim()) qp.set('maxOffset', params.maxOffset.trim());
      } else {
        if (params.year) qp.set('year', params.year);
        if (params.make) qp.set('make', params.make);
        if (params.model) qp.set('model', params.model);
        if (params.trim) qp.set('trim', params.trim);
      }
      const res = await fetch(`${BASE}/api/wheelpros/products?${qp}`, { headers: authOpts.headers });
      const data = await res.json();
      if (res.ok && Array.isArray(data.skus)) {
        setSelected(new Set(data.skus as string[]));
        setAllPagesSelected(true);
      }
    } catch { /* non-fatal */ } finally {
      setSelectingAllPages(false);
    }
  }, [params, searchMode, hideNoImages, BASE, authOpts.headers]);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-gradient">Wheel Pros Catalog</h1>
            <p className="text-muted-foreground mt-1">
              Browse, search, and import Wheel Pros products directly into Shopify.
            </p>
          </div>
          {configured && (
            <div className={cn(
              'flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium border',
              connected
                ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
            )}>
              <div className={cn('w-1.5 h-1.5 rounded-full', connected ? 'bg-emerald-400' : 'bg-amber-400')} />
              {connected ? 'Connected' : 'Auth issue'}
            </div>
          )}
        </div>

        {/* Credential banner */}
        {!configured && (
          <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
            <Info className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-300">WheelPros account not connected</p>
              <p className="text-xs text-amber-400/80 mt-0.5">
                Connect your personal WheelPros account to browse the catalog and import products.
              </p>
              <a
                href={`${BASE}/settings/wheel-pros`}
                className="inline-flex items-center gap-1 mt-2 text-xs text-primary hover:underline"
              >
                Go to WheelPros Settings →
              </a>
            </div>
          </div>
        )}

        {/* Auth error banner */}
        {configured && status?.error && (
          <div className="flex items-start gap-3 bg-red-500/10 border border-red-500/20 rounded-xl p-4">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-700 dark:text-red-300">Authentication failed</p>
              <p className="text-xs text-red-400/80 mt-0.5">{status.error}</p>
            </div>
          </div>
        )}

        {/* Search panel */}
        <div className="glass-panel rounded-2xl p-6 space-y-4">
          {/* Mode tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => setSearchMode('keyword')}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium transition-all',
                searchMode === 'keyword'
                  ? 'bg-primary/15 text-primary border border-primary/30'
                  : 'text-muted-foreground hover:text-foreground border border-border/60 hover:border-border'
              )}
            >
              <Search className="w-3.5 h-3.5 inline-block mr-1.5" />
              Keyword / SKU
            </button>
            <button
              onClick={() => setSearchMode('vehicle')}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium transition-all',
                searchMode === 'vehicle'
                  ? 'bg-primary/15 text-primary border border-primary/30'
                  : 'text-muted-foreground hover:text-foreground border border-border/60 hover:border-border'
              )}
            >
              <Car className="w-3.5 h-3.5 inline-block mr-1.5" />
              Vehicle Fitment
            </button>
          </div>

          <form onSubmit={handleSearch} className="space-y-3">
            {/* Keyword / filter fields */}
            {searchMode === 'keyword' && (
              <div className="space-y-3">
                {/* Row 1: Brand + SKU + Part Number */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Brand</label>
                    <select
                      value={params.brand}
                      onChange={e => setParam('brand', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg bg-muted/50 border border-border text-sm text-foreground focus:outline-none focus:border-primary/50"
                    >
                      <option value="">All brands</option>
                      {(facets?.brands ?? []).map(b => (
                        <option key={b} value={b}>{b}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">SKU</label>
                    <input
                      value={params.sku}
                      onChange={e => setParam('sku', e.target.value)}
                      placeholder="e.g. D61720902650"
                      className="w-full px-3 py-2 rounded-lg bg-muted/50 border border-border text-sm text-foreground placeholder-slate-600 focus:outline-none focus:border-primary/50"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Part Number</label>
                    <input
                      value={params.partNumber}
                      onChange={e => setParam('partNumber', e.target.value)}
                      placeholder="e.g. D61720906"
                      className="w-full px-3 py-2 rounded-lg bg-muted/50 border border-border text-sm text-foreground placeholder-slate-600 focus:outline-none focus:border-primary/50"
                    />
                  </div>
                </div>
                {/* Row 2: Finish + Diameter + Width + Bolt Pattern */}
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Finish</label>
                    <select
                      value={params.finish}
                      onChange={e => setParam('finish', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg bg-muted/50 border border-border text-sm text-foreground focus:outline-none focus:border-primary/50"
                    >
                      <option value="">All finishes</option>
                      {(facets?.finishes ?? []).map(f => (
                        <option key={f} value={f}>{f}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Diameter</label>
                    <select
                      value={params.diameter}
                      onChange={e => setParam('diameter', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg bg-muted/50 border border-border text-sm text-foreground focus:outline-none focus:border-primary/50"
                    >
                      <option value="">Any diameter</option>
                      {(facets?.diameters ?? []).map(d => (
                        <option key={d} value={d}>{d}"</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Width</label>
                    <select
                      value={params.width}
                      onChange={e => setParam('width', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg bg-muted/50 border border-border text-sm text-foreground focus:outline-none focus:border-primary/50"
                    >
                      <option value="">Any width</option>
                      {(facets?.widths ?? []).map(w => (
                        <option key={w} value={w}>{w}"</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Bolt Pattern</label>
                    <select
                      value={params.boltPattern}
                      onChange={e => setParam('boltPattern', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg bg-muted/50 border border-border text-sm text-foreground focus:outline-none focus:border-primary/50"
                    >
                      <option value="">Any bolt pattern</option>
                      {(facets?.boltPatterns ?? []).map(bp => (
                        <option key={bp} value={bp}>{bp}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {/* Row 3: Offset range + Availability */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Min Offset (mm)</label>
                    <input
                      type="number"
                      value={params.minOffset}
                      onChange={e => setParam('minOffset', e.target.value)}
                      placeholder="e.g. -12"
                      className="w-full px-3 py-2 rounded-lg bg-muted/50 border border-border text-sm text-foreground placeholder-slate-600 focus:outline-none focus:border-primary/50"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Max Offset (mm)</label>
                    <input
                      type="number"
                      value={params.maxOffset}
                      onChange={e => setParam('maxOffset', e.target.value)}
                      placeholder="e.g. +45"
                      className="w-full px-3 py-2 rounded-lg bg-muted/50 border border-border text-sm text-foreground placeholder-slate-600 focus:outline-none focus:border-primary/50"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Availability</label>
                    <select
                      value={params.availabilityType}
                      onChange={e => setParam('availabilityType', e.target.value as 'ALL' | 'AVAILABLE' | 'LOCAL')}
                      className="w-full px-3 py-2 rounded-lg bg-muted/50 border border-border text-sm text-foreground focus:outline-none focus:border-primary/50"
                    >
                      <option value="ALL">All products</option>
                      <option value="AVAILABLE">In stock only</option>
                      <option value="LOCAL">Local stock only</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Vehicle fitment cascades */}
            {searchMode === 'vehicle' && (
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Year</label>
                  <select
                    value={params.year}
                    onChange={e => setParam('year', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-muted/50 border border-border text-sm text-foreground focus:outline-none focus:border-primary/50"
                  >
                    <option value="">Select year</option>
                    {years.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Make</label>
                  <select
                    value={params.make}
                    onChange={e => setParam('make', e.target.value)}
                    disabled={!params.year}
                    className="w-full px-3 py-2 rounded-lg bg-muted/50 border border-border text-sm text-foreground focus:outline-none focus:border-primary/50 disabled:opacity-40"
                  >
                    <option value="">Select make</option>
                    {makes.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Model</label>
                  <select
                    value={params.model}
                    onChange={e => setParam('model', e.target.value)}
                    disabled={!params.make}
                    className="w-full px-3 py-2 rounded-lg bg-muted/50 border border-border text-sm text-foreground focus:outline-none focus:border-primary/50 disabled:opacity-40"
                  >
                    <option value="">Select model</option>
                    {filteredModels.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Trim</label>
                  <select
                    value={params.trim}
                    onChange={e => setParam('trim', e.target.value)}
                    disabled={!params.model}
                    className="w-full px-3 py-2 rounded-lg bg-muted/50 border border-border text-sm text-foreground focus:outline-none focus:border-primary/50 disabled:opacity-40"
                  >
                    <option value="">All trims</option>
                    {trims.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3">
              <Button
                type="submit"
                variant="primary"
                disabled={!configured || !connected || searching}
              >
                {searching ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Searching…</>
                ) : (
                  <><Search className="w-4 h-4" /> Search Catalog</>
                )}
              </Button>
              <button
                type="button"
                onClick={() => {
                  setParams({ brand: '', sku: '', partNumber: '', finish: '', diameter: '', width: '', boltPattern: '', minOffset: '', maxOffset: '', availabilityType: 'ALL', year: '', make: '', model: '', trim: '' });
                  setProducts([]);
                  setTotalProducts(0);
                  setSelected(new Set());
                  setPricingMap(new Map());
                  setInventoryMap(new Map());
                  setSearchError(null);
                  setImportResult(null);
                }}
                className="text-xs px-3 py-1.5 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:border-border transition-colors"
              >
                Clear
              </button>
              {products.length > 0 && (
                <span className="text-xs text-muted-foreground">
                  {totalProducts.toLocaleString()} product{totalProducts !== 1 ? 's' : ''} found
                  {enriching && <span className="ml-2 text-primary/70">— loading prices & stock…</span>}
                </span>
              )}
            </div>
          </form>
        </div>

        {/* Re-price panel — shown when WP is connected */}
        {configured && connected && (
          <div className="glass-panel rounded-2xl p-5 space-y-3">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 text-primary" />
                  Re-price Imported Wheels
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Fix prices on wheels already in Shopify using the local catalog CSV (USD pricing × rate). For the most accurate {wpCurrency !== "USD" ? wpCurrency : "AUD"} prices, use <span className="text-muted-foreground font-medium">Reset to WheelPros API Price</span> below — it fetches live prices directly from your account.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
                <ShopifyTargetStoreSelector stores={stores} value={storeId} onChange={setStoreId} label="Shopify store" className="min-w-52" />
                {/* Mode toggle */}
                <div className="flex gap-1 p-0.5 bg-muted/50 border border-border rounded-lg">
                  <button
                    onClick={() => setRepriceMode('rate')}
                    disabled={repricing || previewing}
                    className={`px-3 py-1 rounded text-xs font-medium transition-all disabled:opacity-50 ${repriceMode === 'rate' ? 'bg-primary/20 text-primary border border-primary/30' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    × Rate
                  </button>
                  <button
                    onClick={() => setRepriceMode('flat')}
                    disabled={repricing || previewing}
                    className={`px-3 py-1 rounded text-xs font-medium transition-all disabled:opacity-50 ${repriceMode === 'flat' ? 'bg-primary/20 text-primary border border-primary/30' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    + Flat $
                  </button>
                </div>
                {repriceMode === 'rate' ? (
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {wpCurrency !== "USD" ? `Rate (USD→${wpCurrency})` : "USD→AUD rate"}
                    </span>
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={repriceRate}
                      onChange={e => setRepriceRate(parseFloat(e.target.value) || 1)}
                      disabled={repricing || previewing}
                      className="w-20 px-2 py-1.5 rounded-lg bg-muted/50 border border-border text-sm text-foreground text-right focus:outline-none focus:border-primary/50 disabled:opacity-50"
                    />
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-muted-foreground whitespace-nowrap">Add $</span>
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={repriceFlat}
                      onChange={e => setRepriceFlat(parseFloat(e.target.value) || 0)}
                      disabled={repricing || previewing}
                      className="w-24 px-2 py-1.5 rounded-lg bg-muted/50 border border-border text-sm text-foreground text-right focus:outline-none focus:border-primary/50 disabled:opacity-50"
                    />
                    <span className="text-xs text-muted-foreground">to current price</span>
                  </div>
                )}
                <Button
                  variant="outline"
                  disabled={!storeId || repricing || previewing}
                  onClick={runPreview}
                >
                  {previewing ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Previewing…</>
                  ) : (
                    <><Eye className="w-4 h-4" /> Preview</>
                  )}
                </Button>
                <Button
                  variant="primary"
                  disabled={!storeId || repricing || previewing}
                  onClick={runReprice}
                >
                  {repricing ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Re-pricing…</>
                  ) : (
                    <><RefreshCw className="w-4 h-4" /> Apply</>
                  )}
                </Button>
              </div>
            </div>

            {/* Reprice progress */}
            {repricing && repriceProgress && (repriceProgress.total > 0 || repriceProgress.status) && (
              <div className="space-y-2">
                {repriceProgress.status && (
                  <p className="text-xs text-muted-foreground">{repriceProgress.status}</p>
                )}
                {repriceProgress.total > 0 && (
                  <>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>Checking {repriceProgress.current} / {repriceProgress.total} products…</span>
                      <span>{Math.round((repriceProgress.current / repriceProgress.total) * 100)}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-muted/50 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-primary to-emerald-400 transition-all"
                        style={{ width: `${(repriceProgress.current / repriceProgress.total) * 100}%` }}
                      />
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Reprice result */}
            {repriceResult && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-4 flex-wrap">
                  {repriceResult.updatedVariants > 0 && (
                    <span className="flex items-center gap-1.5 text-sm text-emerald-400">
                      <CheckCircle className="w-4 h-4" />
                      {repriceResult.updatedVariants} variant{repriceResult.updatedVariants !== 1 ? 's' : ''} updated
                    </span>
                  )}
                  {repriceResult.unchangedVariants > 0 && (
                    <span className="text-sm text-muted-foreground">
                      {repriceResult.unchangedVariants} already correct
                    </span>
                  )}
                  {repriceResult.updatedVariants === 0 && repriceResult.unchangedVariants === 0 && repriceResult.errorCount === 0 && (
                    <span className="text-sm text-muted-foreground">No imported products found for this store.</span>
                  )}
                  {repriceResult.errorCount > 0 && (
                    <span className="flex items-center gap-1.5 text-sm text-red-400">
                      <AlertCircle className="w-4 h-4" />
                      {repriceResult.errorCount} error{repriceResult.errorCount !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                {repriceResult.errors.length > 0 && (
                  <div className="space-y-1 max-h-28 overflow-y-auto custom-scrollbar">
                    {repriceResult.errors.map((e, i) => (
                      <p key={i} className="text-xs text-red-400/80">
                        <span className="font-mono">{e.sku}</span>: {e.error}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Preview table */}
            {repricePreview !== null && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <Eye className="w-3.5 h-3.5" />
                    {repricePreview.length === 0
                      ? 'All prices are already up to date at this rate.'
                      : `${repricePreview.length} variant${repricePreview.length !== 1 ? 's' : ''} across ${new Set(repricePreview.map(r => r.productTitle)).size} product${new Set(repricePreview.map(r => r.productTitle)).size !== 1 ? 's' : ''} would change`}
                  </p>
                  <button
                    onClick={() => setRepricePreview(null)}
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Dismiss
                  </button>
                </div>
                {repricePreview.length > 0 && (
                  <div className="max-h-96 overflow-y-auto custom-scrollbar rounded-xl border border-border">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-card/95 backdrop-blur">
                        <tr className="text-left text-muted-foreground border-b border-border">
                          <th className="px-3 py-2 font-medium">Product / Variant</th>
                          <th className="px-3 py-2 font-medium text-right">Current Price</th>
                          <th className="px-3 py-2 font-medium text-right">New Price</th>
                          <th className="px-3 py-2 font-medium text-right">Current RRP</th>
                          <th className="px-3 py-2 font-medium text-right">New RRP</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {(() => {
                          const grouped: Record<string, typeof repricePreview> = {};
                          for (const row of repricePreview) {
                            (grouped[row.productTitle] ??= []).push(row);
                          }
                          return Object.entries(grouped).map(([title, rows]) => (
                            <React.Fragment key={title}>
                              <tr className="bg-muted/50">
                                <td colSpan={5} className="px-3 py-1.5 font-semibold text-foreground truncate max-w-0">
                                  {title}
                                </td>
                              </tr>
                              {rows.map((row) => {
                                const priceDiff = row.newPrice - row.currentPrice;
                                const priceCls = priceDiff > 0.005 ? 'text-emerald-400' : priceDiff < -0.005 ? 'text-red-400' : 'text-muted-foreground';
                                const rrpDiff = row.newCompareAt - row.currentCompareAt;
                                const rrpCls = rrpDiff > 0.005 ? 'text-emerald-400' : rrpDiff < -0.005 ? 'text-red-400' : 'text-muted-foreground';
                                const fmt = (n: number) => `$${n.toFixed(2)}`;
                                return (
                                  <tr key={row.sku} className="hover:bg-muted/30 transition-colors">
                                    <td className="px-3 py-1.5 pl-5 text-foreground truncate max-w-0">
                                      <span className="text-muted-foreground mr-1.5">{row.variantTitle || row.sku}</span>
                                    </td>
                                    <td className="px-3 py-1.5 text-right text-muted-foreground tabular-nums">{fmt(row.currentPrice)}</td>
                                    <td className={`px-3 py-1.5 text-right tabular-nums font-medium ${priceCls}`}>{fmt(row.newPrice)}</td>
                                    <td className="px-3 py-1.5 text-right text-muted-foreground tabular-nums">{fmt(row.currentCompareAt)}</td>
                                    <td className={`px-3 py-1.5 text-right tabular-nums font-medium ${rrpCls}`}>{fmt(row.newCompareAt)}</td>
                                  </tr>
                                );
                              })}
                            </React.Fragment>
                          ));
                        })()}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Reprice history panel */}
        {configured && connected && storeId && repriceLogs.length > 0 && (
          <div className="glass-panel rounded-2xl p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <History className="w-4 h-4 text-primary" />
                Reprice History
              </h2>
              <span className="text-xs text-muted-foreground">{repriceLogs.length} run{repriceLogs.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="space-y-2">
              {repriceLogs.map((log) => {
                const isOpen = expandedLogId === log.id;
                const date = new Date(log.ranAt);
                const fmt = (n: number) => `$${n.toFixed(2)}`;
                const grouped: Record<string, RepriceChange[]> = {};
                for (const c of log.changes) {
                  (grouped[c.productTitle] ??= []).push(c);
                }
                return (
                  <div key={log.id} className="rounded-xl border border-border overflow-hidden">
                    <button
                      className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-muted/30 transition-colors text-left"
                      onClick={() => setExpandedLogId(isOpen ? null : log.id)}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <ChevronRight className={cn("w-3.5 h-3.5 text-muted-foreground flex-shrink-0 transition-transform", isOpen && "rotate-90")} />
                        <div className="min-w-0">
                          <span className="text-sm text-foreground font-medium">
                            {fmtDate(date)}
                            {' '}
                            <span className="text-muted-foreground font-normal">at</span>
                            {' '}
                            {date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0 text-xs">
                        <span className="text-muted-foreground">Rate: <span className="text-foreground font-mono">{log.fxRate.toFixed(2)}×</span></span>
                        <span className="text-emerald-400 font-medium">{log.updatedVariants} updated</span>
                        {log.errorCount > 0 && (
                          <span className="text-red-400">{log.errorCount} error{log.errorCount !== 1 ? 's' : ''}</span>
                        )}
                      </div>
                    </button>

                    {isOpen && (
                      <div className="border-t border-border">
                        {log.changes.length > 0 ? (
                          <div className="max-h-96 overflow-y-auto custom-scrollbar">
                            <table className="w-full text-xs">
                              <thead className="sticky top-0 bg-card/95 backdrop-blur">
                                <tr className="text-left text-muted-foreground border-b border-border">
                                  <th className="px-3 py-2 font-medium">Product / Variant</th>
                                  <th className="px-3 py-2 font-medium text-right">Old Price</th>
                                  <th className="px-3 py-2 font-medium text-right">New Price</th>
                                  <th className="px-3 py-2 font-medium text-right">Old RRP</th>
                                  <th className="px-3 py-2 font-medium text-right">New RRP</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-white/5">
                                {Object.entries(grouped).map(([title, rows]) => (
                                  <React.Fragment key={title}>
                                    <tr className="bg-muted/50">
                                      <td colSpan={5} className="px-3 py-1.5 font-semibold text-foreground truncate max-w-0">
                                        {title}
                                      </td>
                                    </tr>
                                    {rows.map((row) => {
                                      const priceDiff = row.newPrice - row.oldPrice;
                                      const priceCls = priceDiff > 0.005 ? 'text-emerald-400' : priceDiff < -0.005 ? 'text-red-400' : 'text-muted-foreground';
                                      const rrpDiff = row.newCompareAt - row.oldCompareAt;
                                      const rrpCls = rrpDiff > 0.005 ? 'text-emerald-400' : rrpDiff < -0.005 ? 'text-red-400' : 'text-muted-foreground';
                                      return (
                                        <tr key={row.sku} className="hover:bg-muted/30 transition-colors">
                                          <td className="px-3 py-1.5 pl-5 truncate max-w-0">
                                            <span className="text-muted-foreground mr-1.5">{row.variantTitle || row.sku}</span>
                                          </td>
                                          <td className="px-3 py-1.5 text-right text-muted-foreground tabular-nums">{fmt(row.oldPrice)}</td>
                                          <td className={`px-3 py-1.5 text-right tabular-nums font-medium ${priceCls}`}>{fmt(row.newPrice)}</td>
                                          <td className="px-3 py-1.5 text-right text-muted-foreground tabular-nums">{fmt(row.oldCompareAt)}</td>
                                          <td className={`px-3 py-1.5 text-right tabular-nums font-medium ${rrpCls}`}>{fmt(row.newCompareAt)}</td>
                                        </tr>
                                      );
                                    })}
                                  </React.Fragment>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="px-4 py-3 text-xs text-muted-foreground">No change details recorded for this run.</p>
                        )}
                        {log.errors.length > 0 && (
                          <div className="border-t border-border px-4 py-2 space-y-1">
                            <p className="text-xs text-red-400 font-medium">Errors</p>
                            {log.errors.map((e, i) => (
                              <p key={i} className="text-xs text-red-400/70">
                                <span className="font-mono">{e.sku}</span>: {e.error}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Reset to API price panel */}
        {configured && connected && (
          <div className="glass-panel rounded-2xl p-5 space-y-3">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 text-primary" />
                  Reset to WheelPros API Price
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Fetches live MAP/MSRP from the WheelPros API for every wheel SKU and applies the exchange rate. Use this to restore canonical pricing after a custom adjustment.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
                <ShopifyTargetStoreSelector stores={stores} value={storeId} onChange={setStoreId} label="Shopify store" className="min-w-52" />
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {wpCurrency !== "USD" ? `Rate (WP→${wpCurrency})` : "USD→AUD rate"}
                  </span>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={resetRate}
                    onChange={e => setResetRate(parseFloat(e.target.value) || 1)}
                    disabled={resetting || resetPreviewing}
                    className="w-20 px-2 py-1.5 rounded-lg bg-muted/50 border border-border text-sm text-foreground text-right focus:outline-none focus:border-primary/50 disabled:opacity-50"
                  />
                </div>
                <Button
                  variant="outline"
                  disabled={!storeId || resetting || resetPreviewing}
                  onClick={runResetPreview}
                >
                  {resetPreviewing ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Previewing…</>
                  ) : (
                    <><Eye className="w-4 h-4" /> Preview</>
                  )}
                </Button>
                <Button
                  variant="primary"
                  disabled={!storeId || resetting || resetPreviewing}
                  onClick={runReset}
                >
                  {resetting ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Resetting…</>
                  ) : (
                    <><RefreshCw className="w-4 h-4" /> Reset Prices</>
                  )}
                </Button>
              </div>
            </div>

            {/* Progress */}
            {(resetting || resetPreviewing) && resetProgress && (resetProgress.total > 0 || resetProgress.status) && (
              <div className="space-y-2">
                {resetProgress.status && (
                  <p className="text-xs text-muted-foreground">{resetProgress.status}</p>
                )}
                {resetProgress.total > 0 && (
                  <>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>Checking {resetProgress.current} / {resetProgress.total} products…</span>
                      <span>{Math.round((resetProgress.current / resetProgress.total) * 100)}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-muted/50 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-primary to-emerald-400 transition-all"
                        style={{ width: `${(resetProgress.current / resetProgress.total) * 100}%` }}
                      />
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Result */}
            {resetResult && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-4 flex-wrap">
                  {resetResult.updatedVariants > 0 && (
                    <span className="flex items-center gap-1.5 text-sm text-emerald-400">
                      <CheckCircle className="w-4 h-4" />
                      {resetResult.updatedVariants} variant{resetResult.updatedVariants !== 1 ? 's' : ''} updated
                    </span>
                  )}
                  {resetResult.unchangedVariants > 0 && (
                    <span className="text-sm text-muted-foreground">
                      {resetResult.unchangedVariants} already correct
                    </span>
                  )}
                  {resetResult.updatedVariants === 0 && resetResult.unchangedVariants === 0 && resetResult.errorCount === 0 && (
                    <span className="text-sm text-muted-foreground">No products found for this store.</span>
                  )}
                  {resetResult.errorCount > 0 && (
                    <span className="flex items-center gap-1.5 text-sm text-red-400">
                      <AlertCircle className="w-4 h-4" />
                      {resetResult.errorCount} SKU{resetResult.errorCount !== 1 ? 's' : ''} not in WP API
                    </span>
                  )}
                </div>
                {resetResult.errors.length > 0 && (
                  <div className="space-y-1 max-h-28 overflow-y-auto custom-scrollbar">
                    {resetResult.errors.map((e, i) => (
                      <p key={i} className="text-xs text-red-400/80">
                        <span className="font-mono">{e.sku}</span>: {e.error}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Preview table */}
            {resetPreview !== null && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <Eye className="w-3.5 h-3.5" />
                    {resetPreview.length === 0
                      ? 'All prices already match the WheelPros API at this rate.'
                      : `${resetPreview.length} variant${resetPreview.length !== 1 ? 's' : ''} across ${new Set(resetPreview.map(r => r.productTitle)).size} product${new Set(resetPreview.map(r => r.productTitle)).size !== 1 ? 's' : ''} would change`}
                  </p>
                  <button
                    onClick={() => setResetPreview(null)}
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Dismiss
                  </button>
                </div>
                {resetPreview.length > 0 && (
                  <div className="max-h-96 overflow-y-auto custom-scrollbar rounded-xl border border-border">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-card/95 backdrop-blur">
                        <tr className="text-left text-muted-foreground border-b border-border">
                          <th className="px-3 py-2 font-medium">Product / Variant</th>
                          <th className="px-3 py-2 font-medium text-right">Current Price</th>
                          <th className="px-3 py-2 font-medium text-right">New Price</th>
                          <th className="px-3 py-2 font-medium text-right">Current RRP</th>
                          <th className="px-3 py-2 font-medium text-right">New RRP</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {(() => {
                          const grouped: Record<string, typeof resetPreview> = {};
                          for (const row of resetPreview) {
                            (grouped[row.productTitle] ??= []).push(row);
                          }
                          return Object.entries(grouped).map(([title, rows]) => (
                            <React.Fragment key={title}>
                              <tr className="bg-muted/50">
                                <td colSpan={5} className="px-3 py-1.5 font-semibold text-foreground truncate max-w-0">
                                  {title}
                                </td>
                              </tr>
                              {rows.map((row) => {
                                const priceDiff = row.newPrice - row.currentPrice;
                                const priceCls = priceDiff > 0.005 ? 'text-emerald-400' : priceDiff < -0.005 ? 'text-red-400' : 'text-muted-foreground';
                                const rrpDiff = row.newCompareAt - row.currentCompareAt;
                                const rrpCls = rrpDiff > 0.005 ? 'text-emerald-400' : rrpDiff < -0.005 ? 'text-red-400' : 'text-muted-foreground';
                                const fmt = (n: number) => `$${n.toFixed(2)}`;
                                return (
                                  <tr key={row.sku} className="hover:bg-muted/30 transition-colors">
                                    <td className="px-3 py-1.5 pl-5 text-foreground truncate max-w-0">
                                      <span className="text-muted-foreground mr-1.5">{row.variantTitle || row.sku}</span>
                                    </td>
                                    <td className="px-3 py-1.5 text-right text-muted-foreground tabular-nums">{fmt(row.currentPrice)}</td>
                                    <td className={`px-3 py-1.5 text-right tabular-nums font-medium ${priceCls}`}>{fmt(row.newPrice)}</td>
                                    <td className="px-3 py-1.5 text-right text-muted-foreground tabular-nums">{fmt(row.currentCompareAt)}</td>
                                    <td className={`px-3 py-1.5 text-right tabular-nums font-medium ${rrpCls}`}>{fmt(row.newCompareAt)}</td>
                                  </tr>
                                );
                              })}
                            </React.Fragment>
                          ));
                        })()}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Update cost price (NIP) panel */}
        {configured && connected && (
          <div className="glass-panel rounded-2xl p-5 space-y-3">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 text-primary" />
                  Update Cost Price (NIP)
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Writes the WheelPros NIP (Net Invoice Price / dealer cost) to Shopify's "Cost per item" field for every wheel variant. Useful for products imported before cost-price support was added.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
                <ShopifyTargetStoreSelector stores={stores} value={storeId} onChange={setStoreId} label="Shopify store" className="min-w-52" />
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {wpCurrency !== "USD" ? `Rate (WP→${wpCurrency})` : "USD→AUD rate"}
                  </span>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={costRate}
                    onChange={e => setCostRate(parseFloat(e.target.value) || 1)}
                    disabled={costUpdating || costPreviewing}
                    className="w-20 px-2 py-1.5 rounded-lg bg-muted/50 border border-border text-sm text-foreground text-right focus:outline-none focus:border-primary/50 disabled:opacity-50"
                  />
                </div>
                <Button
                  variant="outline"
                  disabled={!storeId || costUpdating || costPreviewing}
                  onClick={runCostPreview}
                >
                  {costPreviewing ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Previewing…</>
                  ) : (
                    <><Eye className="w-4 h-4" /> Preview</>
                  )}
                </Button>
                <Button
                  variant="primary"
                  disabled={!storeId || costUpdating || costPreviewing}
                  onClick={runCostUpdate}
                >
                  {costUpdating ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Updating…</>
                  ) : (
                    <><RefreshCw className="w-4 h-4" /> Update Cost</>
                  )}
                </Button>
              </div>
            </div>

            {/* Progress */}
            {(costUpdating || costPreviewing) && costProgress && (costProgress.total > 0 || costProgress.status) && (
              <div className="space-y-2">
                {costProgress.status && (
                  <p className="text-xs text-muted-foreground">{costProgress.status}</p>
                )}
                {costProgress.total > 0 && (
                  <>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>Processing {costProgress.current} / {costProgress.total} products…</span>
                      <span>{Math.round((costProgress.current / costProgress.total) * 100)}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-muted/50 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-primary to-emerald-400 transition-all"
                        style={{ width: `${(costProgress.current / costProgress.total) * 100}%` }}
                      />
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Result */}
            {costResult && (
              <div className="flex items-center gap-4 flex-wrap">
                {costResult.updatedVariants > 0 && (
                  <span className="flex items-center gap-1.5 text-sm text-emerald-400">
                    <CheckCircle className="w-4 h-4" />
                    {costResult.updatedVariants} variant{costResult.updatedVariants !== 1 ? 's' : ''} updated
                  </span>
                )}
                {costResult.unchangedVariants > 0 && (
                  <span className="text-sm text-muted-foreground">
                    {costResult.unchangedVariants} already correct
                  </span>
                )}
                {costResult.skippedNoNip > 0 && (
                  <span className="text-sm text-muted-foreground">
                    {costResult.skippedNoNip} skipped — no NIP in WP
                  </span>
                )}
                {costResult.errorCount > 0 && (
                  <span className="text-sm text-amber-400">
                    {costResult.errorCount} write error{costResult.errorCount !== 1 ? 's' : ''} — check logs
                  </span>
                )}
                {costResult.updatedVariants === 0 && costResult.unchangedVariants === 0 && costResult.skippedNoNip === 0 && (
                  <span className="text-sm text-muted-foreground">No wheel variants found for this store.</span>
                )}
              </div>
            )}

            {/* Preview table */}
            {costPreview !== null && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <Eye className="w-3.5 h-3.5" />
                    {costPreview.length === 0
                      ? 'No variants with NIP pricing found in WheelPros API.'
                      : `${costPreview.length} variant${costPreview.length !== 1 ? 's' : ''} across ${new Set(costPreview.map(r => r.productTitle)).size} product${new Set(costPreview.map(r => r.productTitle)).size !== 1 ? 's' : ''} will have cost updated`}
                  </p>
                  <button
                    onClick={() => setCostPreview(null)}
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Dismiss
                  </button>
                </div>
                {costPreview.length > 0 && (
                  <div className="max-h-96 overflow-y-auto custom-scrollbar rounded-xl border border-border">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-card/95 backdrop-blur">
                        <tr className="text-left text-muted-foreground border-b border-border">
                          <th className="px-3 py-2 font-medium">Product / Variant</th>
                          <th className="px-3 py-2 font-medium text-right">WP NIP (USD)</th>
                          <th className="px-3 py-2 font-medium text-right">New Cost (AUD)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {(() => {
                          const grouped: Record<string, typeof costPreview> = {};
                          for (const row of costPreview) {
                            (grouped[row.productTitle] ??= []).push(row);
                          }
                          return Object.entries(grouped).map(([title, rows]) => (
                            <React.Fragment key={title}>
                              <tr className="bg-muted/50">
                                <td colSpan={3} className="px-3 py-1.5 font-semibold text-foreground truncate max-w-0">
                                  {title}
                                </td>
                              </tr>
                              {rows.map((row) => (
                                <tr key={row.sku} className="hover:bg-muted/30 transition-colors">
                                  <td className="px-3 py-1.5 pl-5 text-foreground truncate max-w-0">
                                    <span className="text-muted-foreground mr-1.5">{row.variantTitle || row.sku}</span>
                                  </td>
                                  <td className="px-3 py-1.5 text-right text-muted-foreground tabular-nums">${row.nipUsd.toFixed(2)}</td>
                                  <td className="px-3 py-1.5 text-right text-emerald-400 tabular-nums font-medium">${row.newCostAud.toFixed(2)}</td>
                                </tr>
                              ))}
                            </React.Fragment>
                          ));
                        })()}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Backfill images panel — shown when WP is connected */}
        {configured && connected && (
          <div className="glass-panel rounded-2xl p-5 space-y-3">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 text-primary" />
                  Backfill Variant Images
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Assign finish-specific images to variants on products imported before per-finish image linking was added.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
                <ShopifyTargetStoreSelector stores={stores} value={storeId} onChange={setStoreId} label="Shopify store" className="min-w-52" />
                <Button
                  variant="primary"
                  disabled={!storeId || backfilling}
                  onClick={runBackfill}
                >
                  {backfilling ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Backfilling…</>
                  ) : (
                    <><RefreshCw className="w-4 h-4" /> Backfill Images</>
                  )}
                </Button>
              </div>
            </div>

            {/* Backfill progress */}
            {backfilling && backfillProgress && (backfillProgress.total > 0 || backfillProgress.status) && (
              <div className="space-y-2">
                {backfillProgress.status && (
                  <p className="text-xs text-muted-foreground">{backfillProgress.status}</p>
                )}
                {backfillProgress.total > 0 && (
                  <>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>Checking {backfillProgress.current} / {backfillProgress.total} products…</span>
                      <span>{Math.round((backfillProgress.current / backfillProgress.total) * 100)}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-muted/50 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-primary to-emerald-400 transition-all"
                        style={{ width: `${(backfillProgress.current / backfillProgress.total) * 100}%` }}
                      />
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Backfill result */}
            {backfillResult && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-4 flex-wrap">
                  {backfillResult.linked > 0 && (
                    <span className="flex items-center gap-1.5 text-sm text-emerald-400">
                      <CheckCircle className="w-4 h-4" />
                      {backfillResult.linked} variant{backfillResult.linked !== 1 ? 's' : ''} linked
                    </span>
                  )}
                  {backfillResult.alreadyLinked > 0 && (
                    <span className="text-sm text-muted-foreground">
                      {backfillResult.alreadyLinked} already linked
                    </span>
                  )}
                  {backfillResult.noImage > 0 && (
                    <span className="text-sm text-muted-foreground">
                      {backfillResult.noImage} without matching image
                    </span>
                  )}
                  {backfillResult.linked === 0 && backfillResult.alreadyLinked === 0 && backfillResult.noImage === 0 && backfillResult.errorCount === 0 && (
                    <span className="text-sm text-muted-foreground">No imported products found for this store.</span>
                  )}
                  {backfillResult.linked === 0 && backfillResult.noImage > 0 && backfillResult.alreadyLinked === 0 && (
                    <span className="text-sm text-amber-400">No image matches found — products may lack catalog images.</span>
                  )}
                  {backfillResult.errorCount > 0 && (
                    <span className="flex items-center gap-1.5 text-sm text-red-400">
                      <AlertCircle className="w-4 h-4" />
                      {backfillResult.errorCount} error{backfillResult.errorCount !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                {backfillResult.errors.length > 0 && (
                  <div className="space-y-1 max-h-28 overflow-y-auto custom-scrollbar">
                    {backfillResult.errors.map((e, i) => (
                      <p key={i} className="text-xs text-red-400/80">
                        <span className="font-mono">{e.sku}</span>: {e.error}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Search error */}
        {searchError && (
          <div className="flex items-center gap-2 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {searchError}
          </div>
        )}

        {/* Results toolbar + grid */}
        {products.length > 0 && (
          <>
            {/* Toolbar */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <button
                  onClick={toggleAll}
                  className="text-xs px-3 py-1.5 rounded-lg border border-border hover:border-border text-muted-foreground hover:text-foreground transition-colors"
                >
                  {allGroupsSelected ? 'Deselect all' : 'Select all'}
                </button>
                {selected.size > 0 && (
                  <span className="text-xs text-primary font-medium">
                    {(() => {
                      const selGroups = groupedDisplayed.filter(g => g.skus.some(s => selected.has(s.sku))).length;
                      if (allPagesSelected) {
                        return `All ${selected.size.toLocaleString()} SKUs across all pages selected`;
                      }
                      return selGroups === 1
                        ? `${selGroups} model (${selected.size} SKU${selected.size !== 1 ? 's' : ''})`
                        : `${selGroups} models (${selected.size} SKU${selected.size !== 1 ? 's' : ''})`;
                    })()}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <ShopifyTargetStoreSelector stores={stores} value={storeId} onChange={setStoreId} label="Shopify store" className="min-w-52" />

                {/* Product structure */}
                <select
                  value={productStructure}
                  onChange={e => setProductStructure(e.target.value as 'grouped' | 'individual')}
                  className="px-3 py-1.5 rounded-lg bg-muted/50 border border-border text-sm text-foreground focus:outline-none focus:border-primary/50"
                  title="How to structure products in Shopify"
                >
                  <option value="grouped">Grouped (variants per model)</option>
                  <option value="individual">Individual (one product per SKU)</option>
                </select>

                {/* Duplicate handling */}
                <select
                  value={duplicateHandling}
                  onChange={e => setDuplicateHandling(e.target.value as 'skip' | 'overwrite' | 'create')}
                  className="px-3 py-1.5 rounded-lg bg-muted/50 border border-border text-sm text-foreground focus:outline-none focus:border-primary/50"
                >
                  <option value="skip">Skip duplicates</option>
                  <option value="overwrite">Overwrite duplicates</option>
                  <option value="create">Always create</option>
                </select>

                {/* Product status (active vs draft) */}
                <select
                  value={productStatus}
                  onChange={e => setProductStatus(e.target.value as 'active' | 'draft')}
                  className="px-3 py-1.5 rounded-lg bg-muted/50 border border-border text-sm text-foreground focus:outline-none focus:border-primary/50"
                >
                  <option value="active">Publish as Active</option>
                  <option value="draft">Save as Draft</option>
                </select>

                {/* Product template selector — shown whenever a store is selected */}
                {storeId && productTemplates.length > 0 && (
                  <select
                    value={productTemplate}
                    onChange={e => setProductTemplate(e.target.value)}
                    disabled={loadingTemplates}
                    className="px-3 py-1.5 rounded-lg bg-muted/50 border border-border text-sm text-foreground focus:outline-none focus:border-primary/50 disabled:opacity-50"
                    title="Product template"
                  >
                    {productTemplates.map(t => (
                      <option key={t.value} value={t.value}>{t.value === '' ? 'Default template' : `Template: ${t.label}`}</option>
                    ))}
                  </select>
                )}

                {/* Exchange rate */}
                <label className="flex items-center gap-1.5 text-sm text-muted-foreground whitespace-nowrap">
                  {wpCurrency !== "USD" ? `Rate (WP→${wpCurrency})` : "USD→AUD rate"}
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={fxRate}
                    onChange={e => setFxRate(parseFloat(e.target.value) || 1)}
                    className="w-16 px-2 py-1.5 rounded-lg bg-muted/50 border border-border text-sm text-foreground focus:outline-none focus:border-primary/50"
                  />
                </label>

                {/* Track quantity toggle */}
                <select
                  value={trackQuantity ? 'yes' : 'no'}
                  onChange={e => setTrackQuantity(e.target.value === 'yes')}
                  className="px-3 py-1.5 rounded-lg bg-muted/50 border border-border text-sm text-foreground focus:outline-none focus:border-primary/50"
                  title="Inventory tracking"
                >
                  <option value="yes">Track inventory</option>
                  <option value="no">Don't track inventory</option>
                </select>

                <Button
                  variant="primary"
                  disabled={selected.size === 0 || !storeId || importing}
                  onClick={runImport}
                >
                  {importing ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Importing…</>
                  ) : (
                    <><ShoppingBag className="w-4 h-4" /> Import to Shopify</>
                  )}
                </Button>
              </div>
            </div>

            {/* Cross-page select-all banner */}
            {allGroupsSelected && totalPages > 1 && !allPagesSelected && (
              <div className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl bg-primary/5 border border-primary/20 text-xs">
                <span className="text-muted-foreground">
                  All <span className="text-foreground font-medium">{products.length}</span> products on this page are selected.
                </span>
                <button
                  onClick={selectAllPages}
                  disabled={selectingAllPages}
                  className="flex items-center gap-1.5 text-primary hover:text-primary/80 font-medium transition-colors disabled:opacity-50"
                >
                  {selectingAllPages
                    ? <><Loader2 className="w-3 h-3 animate-spin" /> Loading…</>
                    : <>Select all {totalProducts.toLocaleString()} products across all {totalPages} pages →</>}
                </button>
              </div>
            )}
            {allPagesSelected && (
              <div className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl bg-emerald-500/5 border border-emerald-500/20 text-xs">
                <span className="text-emerald-400 font-medium">
                  ✓ All {selected.size.toLocaleString()} products across all {totalPages} pages are selected.
                </span>
                <button
                  onClick={() => { setSelected(new Set()); setAllPagesSelected(false); }}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  Clear selection
                </button>
              </div>
            )}

            {/* Import progress bar */}
            {importing && (
              <div className={cn(
                'glass-panel rounded-xl p-4 space-y-2',
                importConnectionLost && 'border border-amber-500/20'
              )}>
                {importConnectionLost && (
                  <div className="flex items-center gap-2 text-xs text-amber-400 mb-1">
                    <RefreshCw className="w-3 h-3 animate-spin shrink-0" />
                    <span>Connection lost — polling for progress…</span>
                  </div>
                )}
                {importProgress ? (
                  <>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>
                        {importConnectionLost ? 'Last known: ' : 'Importing '}
                        {importProgress.current} / {importProgress.total} product groups…
                      </span>
                      <span>{Math.round((importProgress.current / importProgress.total) * 100)}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-muted/50 overflow-hidden">
                      <div
                        className={cn(
                          'h-full rounded-full transition-all',
                          importConnectionLost
                            ? 'bg-gradient-to-r from-amber-500 to-amber-400'
                            : 'bg-gradient-to-r from-primary to-emerald-400'
                        )}
                        style={{ width: `${(importProgress.current / importProgress.total) * 100}%` }}
                      />
                    </div>
                  </>
                ) : (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
                    <span>{importStatus || 'Preparing import…'}</span>
                  </div>
                )}
              </div>
            )}

            {/* Import result */}
            {importResult && (
              <div className="glass-panel rounded-xl p-4">
                <div className="flex items-center gap-3 flex-wrap">
                  {importResult.successCount > 0 && (
                    <span className="flex items-center gap-1.5 text-sm text-emerald-400">
                      <CheckCircle className="w-4 h-4" />
                      {importResult.successCount} imported to Shopify
                      {productStatus === 'draft' && <span className="text-xs text-muted-foreground">(as drafts)</span>}
                    </span>
                  )}
                  {importResult.skipCount > 0 && (
                    <span className="text-sm text-muted-foreground">{importResult.skipCount} skipped (already exist)</span>
                  )}
                  {importResult.noPriceSkipCount > 0 && (
                    <span className="text-sm text-amber-500/80">{importResult.noPriceSkipCount} skipped (no price)</span>
                  )}
                  {importResult.errorCount > 0 && (
                    <span className="flex items-center gap-1.5 text-sm text-red-400">
                      <AlertCircle className="w-4 h-4" />
                      {importResult.errorCount} failed
                    </span>
                  )}
                  {(importResult.inputDuplicateCount > 0 || importResult.droppedVariantCount > 0) && (
                    <span className="text-xs text-slate-500" title="Duplicate SKUs or variant combos were removed before importing">
                      {importResult.inputDuplicateCount > 0 && `${importResult.inputDuplicateCount} duplicate SKU${importResult.inputDuplicateCount !== 1 ? 's' : ''} removed`}
                      {importResult.inputDuplicateCount > 0 && importResult.droppedVariantCount > 0 && ', '}
                      {importResult.droppedVariantCount > 0 && `${importResult.droppedVariantCount} duplicate variant${importResult.droppedVariantCount !== 1 ? 's' : ''} dropped`}
                    </span>
                  )}
                </div>
                {importResult.errors.length > 0 && (
                  <div className="mt-3 space-y-1 max-h-32 overflow-y-auto custom-scrollbar">
                    {importResult.errors.map((e, i) => (
                      <p key={i} className="text-xs text-red-400/80">
                        <span className="font-mono">{e.sku}</span>: {e.error}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Results search bar + image filter toggle */}
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <input
                  type="text"
                  value={filterText}
                  onChange={e => setFilterText(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && filterText.trim()) {
                      setFilterText('');
                      doSearch(1, filterText.trim());
                    }
                  }}
                  placeholder="Filter this page, or press Enter to search all pages…"
                  className="w-full pl-9 pr-9 py-2.5 rounded-xl bg-muted/50 border border-border text-sm text-foreground placeholder-slate-600 focus:outline-none focus:border-primary/50 transition-colors"
                />
                {filterText && (
                  <button
                    onClick={() => setFilterText('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label="Clear filter"
                  >
                    ✕
                  </button>
                )}
              </div>

              {/* Hide no-image toggle */}
              <button
                onClick={() => {
                  const next = !hideNoImages;
                  setHideNoImages(next);
                  // Re-fetch from the server with the new filter applied.
                  // Pass the next value explicitly because React state hasn't updated yet.
                  if (products.length > 0) doSearch(1, undefined, next);
                }}
                className={cn(
                  'flex items-center gap-2 px-3 py-2.5 rounded-xl border text-xs font-medium whitespace-nowrap transition-all',
                  hideNoImages
                    ? 'bg-primary/15 border-primary/40 text-primary'
                    : 'bg-muted/50 border-border text-muted-foreground hover:border-border hover:text-foreground'
                )}
                title={hideNoImages ? 'Showing only products with images' : 'Show all products (including those without images)'}
              >
                <span className={cn('w-1.5 h-1.5 rounded-full', hideNoImages ? 'bg-primary' : 'bg-slate-600')} />
                Images only
                {/* When OFF: show how many on this page would be hidden as a preview.
                    When ON: the server already filtered — no badge needed. */}
                {!hideNoImages && noImageCount > 0 && (
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-muted/60 text-muted-foreground">
                    {noImageCount} hidden
                  </span>
                )}
              </button>
            </div>

            {/* Filter match count */}
            {filterText && (
              <p className="text-xs text-muted-foreground -mt-3">
                {displayedProducts.length === 0
                  ? 'No matches on this page — press Enter to search all pages'
                  : `${displayedProducts.length} of ${products.length} on this page match — press Enter to search all pages`}
              </p>
            )}

            {/* Product grid — grouped by model */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {groupedDisplayed.map(group => (
                <GroupedProductCard
                  key={group.groupKey}
                  group={group}
                  selected={selected}
                  onToggleSkus={toggleSkus}
                  pricingMap={pricingMap}
                  inventoryMap={inventoryMap}
                  fxRate={fxRate}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={() => doSearch(page - 1)}
                  disabled={page <= 1 || searching}
                  className="px-3 py-1.5 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
                >
                  Previous
                </button>
                <span className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => doSearch(page + 1)}
                  disabled={page >= totalPages || searching}
                  className="px-3 py-1.5 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        {/* Empty state */}
        {!searching && products.length === 0 && !searchError && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 rounded-2xl bg-muted/50 flex items-center justify-center mb-4">
              <Package className="w-8 h-8 text-slate-600" />
            </div>
            <p className="text-muted-foreground font-medium">No products yet</p>
            <p className="text-slate-600 text-sm mt-1">
              {configured && connected
                ? 'Use the search bar above to browse the Wheel Pros catalog.'
                : 'Configure your Wheel Pros credentials to get started.'}
            </p>
          </div>
        )}

      </div>
    </Layout>
  );
}
